import React from "react";
import {
  Badge,
  BlockStack,
  Box,
  Card,
  Collapsible,
  Grid,
  InlineStack,
  List,
  Select,
  Text,
  Tooltip,
} from "@shopify/polaris";
import { BarChart3, ChevronDown, Lock, RefreshCw } from "lucide-react";
import type { AnalyticsData, KbGap, QueryLogItem } from "../types";
import { CHANNEL_DEFAULTS } from "../config";

const InfoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block" }}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

const InfoHint = ({ content }: { content: string }) => (
  <Tooltip content={content} preferredPosition="above">
    <span style={{ cursor: "help" }} className="theme-icon-muted theme-analytics-inline-info">
      <InfoIcon />
    </span>
  </Tooltip>
);

const StoryInfoLabel = ({
  label,
  tip,
  subdued,
  extra,
}: {
  label: string;
  tip: string;
  subdued?: boolean;
  extra?: React.ReactNode;
}) => (
  <span className={`theme-analytics-inline-label${subdued ? " theme-analytics-inline-label-subdued" : ""}`}>
    <span>{label}</span>
    <InfoHint content={tip} />
    {extra}
  </span>
);

const MetricCard = ({
  label,
  value,
  tip,
  locked,
  lockedLabel,
  tone,
}: {
  label: string;
  value: string | number;
  tip: string;
  locked?: boolean;
  lockedLabel?: string;
  tone?: "success" | "caution" | "critical" | "base" | "subdued";
}) => (
  <div className="theme-metric-card">
    <div className="theme-metric-card-surface" data-tone={tone || "base"}>
      <BlockStack gap="200">
        <InlineStack gap="150" blockAlign="center">
          <Text as="p" variant="bodySm" tone="subdued">
            {label}
          </Text>
          <Tooltip content={tip} preferredPosition="above">
            <span style={{ cursor: "help" }} className="theme-icon-muted">
              <InfoIcon />
            </span>
          </Tooltip>
        </InlineStack>
        {locked ? (
          <BlockStack gap="100">
            <InlineStack gap="150" blockAlign="center">
              <Lock size={14} color="#94a3b8" />
              <Text as="h3" variant="headingLg" tone="subdued">
                —
              </Text>
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">
              {lockedLabel || "Higher plan required"}
            </Text>
          </BlockStack>
        ) : (
          <div className="theme-metric-value">
            <Text as="h2" variant="headingXl" tone={tone || "base"}>
              {value ?? "—"}
            </Text>
          </div>
        )}
      </BlockStack>
    </div>
  </div>
);

const Sparkline = ({ data, color = "#312e81" }: { data: number[]; color?: string }) => {
  const max = Math.max(...data, 1);
  const h = 44;
  const barW = 8;
  const gap = 3;
  const totalW = data.length * (barW + gap) - gap;

  return (
    <svg className="theme-sparkline" width={totalW} height={h} style={{ display: "block", overflow: "visible" }}>
      <line x1="0" y1={h - 1} x2={totalW} y2={h - 1} stroke="rgba(148, 163, 184, 0.22)" strokeWidth="1" />
      {data.map((value, index) => {
        const barH = Math.max(3, Math.round((value / max) * h));
        return <rect key={index} x={index * (barW + gap)} y={h - barH} width={barW} height={barH} rx={2} fill={color} opacity={0.65} />;
      })}
    </svg>
  );
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, Math.round(value)));
const toSafeNumber = (value: unknown, fallback = 0) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;
const toSafeNullableNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
type AnalyticsTrendPoint = NonNullable<AnalyticsData["dailyTrend"]>[number];
type AnalyticsTopicPoint = NonNullable<AnalyticsData["topTopics"]>[number];
type AnalyticsChannelPoint = NonNullable<AnalyticsData["channelClickBreakdown"]>[number];
type AnalyticsSuggestionPoint = NonNullable<AnalyticsData["topClickedSuggestions"]>[number];

const formatShortDateRange = (start: Date, end: Date) => {
  const formatDate = (value: Date) =>
    value.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const startLabel = formatDate(start);
  const endLabel = formatDate(end);
  return `${startLabel} - ${endLabel}`;
};

const getDeltaTooltipLabel = (analyticsPeriod: string) => {
  const periodDays = Number.parseInt(analyticsPeriod || "30", 10);
  if (!Number.isFinite(periodDays) || periodDays <= 0) return "Compared with the previous period";

  const today = new Date();
  const currentPeriodEnd = new Date(today);
  currentPeriodEnd.setDate(currentPeriodEnd.getDate() - 1);
  const previousPeriodEnd = new Date(currentPeriodEnd);
  previousPeriodEnd.setDate(previousPeriodEnd.getDate() - periodDays);
  const previousPeriodStart = new Date(previousPeriodEnd);
  previousPeriodStart.setDate(previousPeriodStart.getDate() - (periodDays - 1));

  if (periodDays === 7) {
    return `Compared with last week (${formatShortDateRange(previousPeriodStart, previousPeriodEnd)})`;
  }

  return `Compared with ${formatShortDateRange(previousPeriodStart, previousPeriodEnd)}`;
};

const formatDeltaLabel = (delta: number | null, direction: "up" | "down" = "up") => {
  if (delta == null) return null;
  if (delta === 0) return "no change|neutral";
  const movement = delta > 0 ? "up" : "down";
  const isPositive = direction === "up" ? delta > 0 : delta < 0;
  return `${movement} ${Math.abs(delta)}%|${isPositive ? "positive" : "negative"}`;
};

const DeltaNote = ({
  delta,
  favorableDirection = "up",
  compact,
  iconOnly,
  tooltipContent = "Compared with the previous period",
}: {
  delta: number | null;
  favorableDirection?: "up" | "down";
  compact?: boolean;
  iconOnly?: boolean;
  tooltipContent?: string;
}) => {
  const label = formatDeltaLabel(delta, favorableDirection);
  if (!label) return null;
  const [text, tone] = label.split("|");
  const isUp = delta != null && delta > 0;
  const isFlat = delta === 0;
  const content = (
    <span className={`theme-analytics-story-delta theme-analytics-story-delta-${isFlat ? "neutral" : tone}${compact ? " theme-analytics-story-delta-compact" : ""}${iconOnly ? " theme-analytics-story-delta-icon-only" : ""}`}>
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        {isFlat ? (
          <path d="M2.5 8H13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        ) : isUp ? (
          <path d="M2.5 11.5L6.2 7.8L8.9 10.1L13.5 4.5M13.5 4.5H10.7M13.5 4.5V7.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <path d="M2.5 4.5L6.2 8.2L8.9 5.9L13.5 11.5M13.5 11.5H10.7M13.5 11.5V8.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
      {!iconOnly ? text : null}
    </span>
  );
  const resolvedTooltipContent = iconOnly ? `${text} · ${tooltipContent}` : tooltipContent;

  return compact ? (
    <Tooltip content={resolvedTooltipContent} preferredPosition="above">
      {content}
    </Tooltip>
  ) : (
    <Tooltip content={resolvedTooltipContent} preferredPosition="above">
      {content}
    </Tooltip>
  );
};

const DeltaBadge = ({
  delta,
  favorableDirection = "up",
  tooltipContent = "Compared with the previous period",
}: {
  delta: number | null;
  favorableDirection?: "up" | "down";
  tooltipContent?: string;
}) => {
  const label = formatDeltaLabel(delta, favorableDirection);
  if (!label) return null;
  const [text, tone] = label.split("|");

  return (
    <Tooltip content={tooltipContent} preferredPosition="above">
      <span className={`theme-analytics-coverage-chip theme-analytics-coverage-chip-delta theme-analytics-coverage-chip-delta-${tone}`}>
        {`gaps ${text}`}
      </span>
    </Tooltip>
  );
};

const CircularScore = ({
  value,
  label,
  trend,
  accentId,
  startColor = "#1e3a8a",
  endColor = "#0ea5e9",
}: {
  value: number | null;
  label: string | null;
  trend?: React.ReactNode;
  accentId: string;
  startColor?: string;
  endColor?: string;
}) => {
  const normalized = value == null ? 0 : clampPercent(value);
  const circumference = 188;
  const offset = circumference - (normalized / 100) * circumference;

  return (
    <div className="theme-analytics-ring-wrap">
      <div className="theme-analytics-ring">
        <svg viewBox="0 0 72 72" fill="none">
          <defs>
            <linearGradient id={accentId} x1="0" y1="0" x2="72" y2="72" gradientUnits="userSpaceOnUse">
              <stop stopColor={startColor} />
              <stop offset="1" stopColor={endColor} />
            </linearGradient>
          </defs>
          <circle cx="36" cy="36" r="30" stroke="rgba(14,165,233,0.12)" strokeWidth="7" />
          <circle
            cx="36"
            cy="36"
            r="30"
            stroke={`url(#${accentId})`}
            strokeWidth="7"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 36 36)"
          />
        </svg>
        <span>{value != null ? `${normalized}%` : "—"}</span>
      </div>
      {label && (
        <p className="theme-analytics-ring-label">
          <span>{label}</span>
          {trend}
        </p>
      )}
    </div>
  );
};

const SplitDonut = ({
  segments,
  legendSegments,
  centerValue,
  centerLabel,
  selectedKey,
  onSelect,
  tooltipPrefix,
  variant = "default",
}: {
  segments: Array<{ key?: string; label: string; value: number; color: string }>;
  legendSegments?: Array<{ key?: string; label: string; value: number; color: string }>;
  centerValue: string;
  centerLabel: string;
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
  tooltipPrefix?: string;
  variant?: "default" | "large";
}) => {
  const blockRef = React.useRef<HTMLDivElement | null>(null);
  const total = Math.max(
    segments.reduce((sum, segment) => sum + segment.value, 0),
    1,
  );
  const circumference = 188.5;
  const [hoveredTip, setHoveredTip] = React.useState<{ label: string; x: number; y: number } | null>(null);
  let accumulated = 0;

  const showTip = (event: React.MouseEvent<SVGCircleElement | HTMLButtonElement>, label: string) => {
    const blockRect = blockRef.current?.getBoundingClientRect();
    if (!blockRect) {
      setHoveredTip({ label, x: 0, y: 0 });
      return;
    }
    setHoveredTip({
      label,
      x: event.clientX - blockRect.left,
      y: event.clientY - blockRect.top - 8,
    });
  };

  return (
    <div className={`theme-analytics-donut-block theme-analytics-donut-block-${variant}`} ref={blockRef}>
      {hoveredTip ? (
        <div
          className="theme-analytics-donut-hover-tip"
          style={{ left: `${hoveredTip.x}px`, top: `${hoveredTip.y}px` }}
        >
          {hoveredTip.label}
        </div>
      ) : null}
      <div className={`theme-analytics-donut theme-analytics-donut-${variant}`}>
        <svg viewBox="0 0 72 72" fill="none" className="theme-analytics-donut-svg">
          <circle cx="36" cy="36" r="30" stroke="rgba(226,232,240,0.95)" strokeWidth="10" />
          {segments.map((segment) => {
            const key = segment.key || segment.label;
            const value = Math.max(0, segment.value);
            const segmentLength = (value / total) * circumference;
            const strokeDasharray = `${segmentLength} ${circumference - segmentLength}`;
            const strokeDashoffset = -accumulated;
            accumulated += segmentLength;
            const isSelected = !selectedKey || selectedKey === key;

            return (
              <circle
                key={key}
                cx="36"
                cy="36"
                r="30"
                stroke={segment.color}
                strokeWidth={10}
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="butt"
                transform="rotate(-90 36 36)"
                className={`theme-analytics-donut-segment${onSelect ? " theme-analytics-donut-segment-clickable" : ""}`}
                style={{ opacity: isSelected ? 1 : 0.3 }}
                onClick={onSelect ? () => onSelect(selectedKey === key ? null : key) : undefined}
                onMouseMove={(event) => showTip(event, `${segment.label}: ${segment.value} ${tooltipPrefix || centerLabel}`)}
                onMouseLeave={() => setHoveredTip(null)}
              />
            );
          })}
        </svg>
        <div className="theme-analytics-donut-core">
          <strong>{centerValue}</strong>
          <span>{centerLabel}</span>
        </div>
      </div>
      <div className="theme-analytics-donut-legend">
        {(legendSegments || segments).map((segment) => (
          <button
            key={segment.key || segment.label}
            type="button"
            className={`theme-analytics-donut-legend-row${selectedKey === (segment.key || segment.label) ? " theme-analytics-donut-legend-row-active" : ""}${onSelect ? " theme-analytics-donut-legend-row-clickable" : ""}`}
            onClick={onSelect ? () => onSelect(selectedKey === (segment.key || segment.label) ? null : (segment.key || segment.label)) : undefined}
            onMouseMove={(event) => showTip(event, `${segment.label}: ${segment.value} ${tooltipPrefix || centerLabel}`)}
            onMouseLeave={() => setHoveredTip(null)}
          >
            <span className="theme-analytics-donut-legend-swatch" style={{ background: segment.color }} />
            <span className="theme-analytics-donut-legend-copy">{segment.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const SegmentedPillBar = ({
  segments,
  totalLabel,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  totalLabel: string;
}) => {
  const blockRef = React.useRef<HTMLDivElement | null>(null);
  const total = Math.max(
    segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0),
    1,
  );
  const [hoveredTip, setHoveredTip] = React.useState<{ label: string; x: number; y: number } | null>(null);

  const showTip = (event: React.MouseEvent<HTMLDivElement>, label: string) => {
    const blockRect = blockRef.current?.getBoundingClientRect();
    if (!blockRect) {
      setHoveredTip({ label, x: 0, y: 0 });
      return;
    }
    setHoveredTip({
      label,
      x: event.clientX - blockRect.left,
      y: event.clientY - blockRect.top - 8,
    });
  };

  return (
    <div className="theme-analytics-segmented-wrap" ref={blockRef}>
      {hoveredTip ? (
        <div
          className="theme-analytics-donut-hover-tip"
          style={{ left: `${hoveredTip.x}px`, top: `${hoveredTip.y}px` }}
        >
          {hoveredTip.label}
        </div>
      ) : null}
      <div className="theme-analytics-segmented-track" role="img" aria-label={totalLabel}>
        {segments.map((segment) => {
          const value = Math.max(0, segment.value);
          const percentage = clampPercent((value / total) * 100);
          const isZero = value === 0;
          const isTiny = percentage > 0 && percentage < 10;

          return (
            <div
              key={segment.label}
              className={`theme-analytics-segmented-slice${isTiny ? " theme-analytics-segmented-slice-tiny" : ""}${isZero ? " theme-analytics-segmented-slice-zero" : ""}`}
              style={{
                width: `${isZero ? 0 : percentage}%`,
                background: segment.color,
              }}
              onMouseMove={(event) => showTip(event, `${segment.label}: ${value} ${totalLabel}`)}
              onMouseLeave={() => setHoveredTip(null)}
            >
              <span style={isTiny || isZero ? { color: segment.color } : undefined}>{percentage}%</span>
            </div>
          );
        })}
      </div>
      <div className="theme-analytics-segmented-legend">
        {segments.map((segment) => (
          <span key={segment.label} className="theme-analytics-segmented-legend-item">
            <span className="theme-analytics-donut-legend-swatch" style={{ background: segment.color }} />
            {segment.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const VisitorStoryCard = ({
  analyticsData,
  analyticsPeriod,
  isRepPlus,
  themeColor,
}: {
  analyticsData: AnalyticsData;
  analyticsPeriod: string;
  isRepPlus: boolean;
  themeColor: string;
}) => {
  const deltaTooltip = getDeltaTooltipLabel(analyticsPeriod);
  const [selectedChannelKey, setSelectedChannelKey] = React.useState<string | null>(null);
  const uniqueVisitors = toSafeNumber(analyticsData.uniqueVisitors);
  const widgetOpens = toSafeNumber(analyticsData.widgetOpens);
  const totalChats = toSafeNumber(analyticsData.totalChats);
  const channelClicks = toSafeNumber(analyticsData.channelClicks);
  const totalMessages = toSafeNumber(analyticsData.totalMessages);
  const widgetOpensDelta = toSafeNullableNumber(analyticsData.widgetOpensDelta);
  const totalChatsDelta = toSafeNullableNumber(analyticsData.totalChatsDelta);
  const channelClicksDelta = toSafeNullableNumber(analyticsData.channelClicksDelta);
  const totalMessagesDelta = toSafeNullableNumber(analyticsData.totalMessagesDelta);
  const channelClickThroughRate = toSafeNullableNumber(analyticsData.channelClickThroughRate);
  const storyBars = isRepPlus && analyticsData.dailyTrend?.length
    ? analyticsData.dailyTrend.slice(-7).map((day: AnalyticsTrendPoint, index: number) => ({
        label: ["M", "T", "W", "T", "F", "S", "S"][index] || `${index + 1}`,
        value: toSafeNumber(day.opens),
        tip: "",
        delta: null as number | null,
      }))
    : [
        { label: "Widget Opens", value: widgetOpens, tip: "How many times visitors opened the widget during the selected period.", delta: widgetOpensDelta },
        { label: "Clicks", value: channelClicks, tip: "Total clicks on contact channels such as WhatsApp, Messenger, SMS, or email.", delta: channelClicksDelta },
        { label: "Chats", value: totalChats, tip: "How many assistant conversations were started in this period.", delta: totalChatsDelta },
        { label: "Messages", value: totalMessages, tip: "Total assistant messages exchanged across all chatbot conversations.", delta: totalMessagesDelta },
      ];
  const maxStoryValue = Math.max(...storyBars.map((bar) => bar.value), 1);
  const returnRateValue = toSafeNullableNumber(analyticsData.returnVisitorRate);
  const resolvedReturnRateValue = returnRateValue ?? (uniqueVisitors === 0 ? 0 : null);
  const mobileOpens = toSafeNumber(analyticsData.deviceSplit?.mobile ?? analyticsData.mobileOpens);
  const desktopOpens = toSafeNumber(analyticsData.deviceSplit?.desktop ?? analyticsData.desktopOpens);
  const channelBreakdown = (analyticsData.channelClickBreakdown || [])
    .filter((row: AnalyticsChannelPoint) => toSafeNumber(row?.clicks) > 0)
    .map((row: AnalyticsChannelPoint, index: number) => {
      const id = String(row.channel || "unknown");
      const channelDefault = (CHANNEL_DEFAULTS as Record<string, any>)[id];
      return {
        key: id,
        label: channelDefault?.label || id.replace(/_/g, " "),
        value: toSafeNumber(row.clicks),
        color: channelDefault?.color || [
          themeColor,
          "#38bdf8",
          "#818cf8",
          "#22c55e",
          "#f59e0b",
          "#f97316",
        ][index % 6],
      };
    });
  const topChannelLegend = channelBreakdown.slice(0, 6);
  const selectedChannel = channelBreakdown.find((segment) => segment.key === selectedChannelKey) || null;
  const displayedChannelClicks = selectedChannel ? selectedChannel.value : channelClicks;
  const resetChannelSelection = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest(".theme-analytics-donut-segment-clickable")) return;
    if (target.closest(".theme-analytics-donut-legend-row-clickable")) return;
    setSelectedChannelKey(null);
  };

  return (
    <div className="theme-analytics-story-card theme-analytics-story-card-visitor theme-analytics-story-card-full">
      <div className="theme-analytics-story-visual theme-analytics-story-visual-visitor">
        <div className="theme-analytics-visitor-row theme-analytics-visitor-row-primary">
          <div className="theme-analytics-story-visual-main">
            <div className="theme-analytics-story-head">
              <div>
                <span className="theme-analytics-story-big">{uniqueVisitors}</span>
                <StoryInfoLabel
                  label="unique visitors this period"
                  tip="Distinct visitors who opened or interacted with the widget during the selected period."
                  subdued
                />
              </div>
            </div>
            <div className="theme-analytics-story-columns-shell">
              <div className="theme-analytics-story-columns">
              {storyBars.map((bar) => (
                <div key={bar.label} className="theme-analytics-story-column">
                  <div className="theme-analytics-story-column-plot">
                    <div
                      className="theme-analytics-story-column-fill"
                      style={{
                        height: `${Math.max(10, Math.round((bar.value / maxStoryValue) * 100))}%`,
                        background: `linear-gradient(180deg, color-mix(in srgb, ${themeColor} 45%, white), ${themeColor})`,
                      }}
                    >
                      <span
                        className={`theme-analytics-story-column-value${
                          bar.value / maxStoryValue < 0.24 ? " theme-analytics-story-column-value-outside" : ""
                        }`}
                      >
                        {bar.value}
                        {bar.delta != null ? <DeltaNote delta={bar.delta} compact iconOnly tooltipContent={deltaTooltip} /> : null}
                      </span>
                    </div>
                  </div>
                  <span className="theme-analytics-story-column-label-row">
                    <span className="theme-analytics-story-column-label">{bar.label}</span>
                    {bar.tip ? <InfoHint content={bar.tip} /> : null}
                  </span>
                </div>
              ))}
            </div>
            </div>
          </div>
          <div
            className="theme-analytics-story-side-panel theme-analytics-story-side-panel-compact theme-analytics-story-side-panel-top"
            onClick={resetChannelSelection}
          >
            <StoryInfoLabel
              label="Channel clicks"
              tip="Total contact-channel clicks, with the donut showing which channels visitors used most often."
              extra={
                <Tooltip content="Click a slice to inspect a channel. Click empty space to show total again." preferredPosition="above">
                  <button type="button" className="theme-analytics-inline-hint-badge" aria-label="Channel click chart hint">
                    Hint
                  </button>
                </Tooltip>
              }
            />
            {channelBreakdown.length > 0 ? (
              <>
                <SplitDonut
                  centerValue={`${displayedChannelClicks}`}
                  centerLabel="clicks"
                  segments={channelBreakdown}
                  selectedKey={selectedChannelKey}
                  onSelect={setSelectedChannelKey}
                  tooltipPrefix="clicks"
                  legendSegments={topChannelLegend}
                  variant="large"
                />
              </>
            ) : (
              <div className="theme-analytics-gap-empty">Channel distribution will appear after visitors click your contact options.</div>
            )}
          </div>
        </div>
        <div className="theme-analytics-visitor-row theme-analytics-visitor-row-secondary">
          <div className="theme-analytics-story-side-panel theme-analytics-story-side-panel-compact">
            <StoryInfoLabel
              label="Device split"
              tip="How widget opens are distributed between mobile and desktop visitors in this period."
            />
            <SegmentedPillBar
              totalLabel="opens"
              segments={[
                { label: "Mobile", value: mobileOpens, color: themeColor },
                { label: "Desktop", value: desktopOpens, color: "color-mix(in srgb, var(--analytics-accent) 32%, white)" },
              ]}
            />
          </div>
          <div className="theme-analytics-story-side-panel theme-analytics-story-side-panel-compact">
            <StoryInfoLabel
              label="Channel click-through rate"
              tip="How often visitors who opened Reach chose a support channel."
            />
            <div className="theme-analytics-nested-track">
              <div className="theme-analytics-nested-header">
                <span className="theme-analytics-nested-pill">
                  {channelClickThroughRate != null ? `${channelClickThroughRate}% click-through` : "No click-through yet"}
                </span>
                <DeltaNote delta={channelClicksDelta} compact iconOnly tooltipContent={deltaTooltip} />
              </div>

              <div
                className="theme-analytics-nested-bar"
                aria-label={`Channel click-through track showing ${channelClicks} channel clicks out of ${widgetOpens} widget opens`}
              >
                <div
                  className="theme-analytics-nested-fill"
                  style={{ width: `${Math.max(0, Math.min(100, channelClickThroughRate ?? 0))}%` }}
                >
                  {(channelClickThroughRate ?? 0) > 15 && (
                    <span className="theme-analytics-nested-fill-label">{channelClicks}</span>
                  )}
                </div>
              </div>

              <div className="theme-analytics-nested-stats">
                <div className="theme-analytics-nested-stat">
                  <span>Widget opens</span>
                  <strong>{widgetOpens}</strong>
                </div>
                <div className="theme-analytics-nested-stat theme-analytics-nested-stat-highlight">
                  <span>Channel clicks</span>
                  <strong>{channelClicks}</strong>
                </div>
              </div>
            </div>
          </div>
          <div className="theme-analytics-story-side-panel theme-analytics-story-side-panel-compact">
            <StoryInfoLabel
              label="Return visitors"
              tip="How often the visitors interacting with Reach were returning visitors."
            />
            <CircularScore
              value={resolvedReturnRateValue}
              label={null}
              accentId="dashboard-analytics-visitor-return"
              startColor={themeColor}
              endColor="#93c5fd"
            />
            <div className="theme-analytics-story-mini-note">Return Rate</div>
          </div>
        </div>
      </div>
      <div className="theme-analytics-story-body">
        <span className="theme-analytics-tier-pill">Visitor Insights</span>
        <h3>See where engagement is actually happening</h3>
        <p>
          A cleaner storefront view of widget opens, chat starts, channel clicks, total messages, device split,
          and return behavior for the current period.
        </p>
      </div>
    </div>
  );
};

const ChatbotStoryCard = ({
  analyticsData,
  themeColor,
  analyticsPeriod,
}: {
  analyticsData: AnalyticsData;
  themeColor: string;
  analyticsPeriod: string;
}) => {
  const deltaTooltip = getDeltaTooltipLabel(analyticsPeriod);
  const feedbackScore = toSafeNullableNumber(analyticsData.feedbackScore);
  const feedbackScoreDelta = toSafeNullableNumber(analyticsData.feedbackScoreDelta);
  const matchRate = toSafeNullableNumber(analyticsData.matchRate);
  const matchRateDelta = toSafeNullableNumber(analyticsData.matchRateDelta);
  const totalMessages = toSafeNumber(analyticsData.totalMessages);
  const totalChats = toSafeNumber(analyticsData.totalChats);

  const suggestionClicks = toSafeNumber(analyticsData.suggestionClicks);
  const suggestionEngagementRate = toSafeNullableNumber(analyticsData.suggestionEngagementRate);
  const averageMessages = totalChats > 0
    ? (totalMessages / totalChats).toFixed(1)
    : "0.0";

  return (
    <div className="theme-analytics-story-card theme-analytics-story-card-perf theme-analytics-story-card-full">
      <div className="theme-analytics-story-visual theme-analytics-story-visual-perf">
        <div className="theme-analytics-story-side-panel theme-analytics-story-stack-stats theme-analytics-story-side-panel-activity">
          <StoryInfoLabel
            label="Conversation activity"
            tip="The interaction volume the assistant handled during this period."
          />
          <div className="theme-analytics-story-stack-stat">
            <strong>{totalChats}</strong>
            <StoryInfoLabel
              label="Conversations Started"
              tip="How many new assistant conversations were opened during the selected period."
              subdued
            />
          </div>
          <div className="theme-analytics-story-stack-stat">
            <strong>{totalMessages}</strong>
            <StoryInfoLabel
              label="Messages in Period"
              tip="The total number of assistant messages exchanged across all chatbot conversations in this period."
              subdued
            />
          </div>
          <div className="theme-analytics-story-stack-stat">
            <strong>{averageMessages}</strong>
            <StoryInfoLabel
              label="Avg Messages Per Chat"
              tip="Average assistant message volume per conversation for the selected period."
              subdued
            />
          </div>
          <div className="theme-analytics-story-stack-stat">
            <strong>{suggestionClicks}</strong>
            <StoryInfoLabel
              label="Suggestion Clicks"
              tip="How many times visitors tapped a suggested prompt or quick action in the assistant."
              subdued
            />
          </div>
        </div>
        <div className="theme-analytics-perf-score-row">
          <div className="theme-analytics-story-side-panel theme-analytics-story-side-panel-compact theme-analytics-story-side-panel-score">
            <StoryInfoLabel
              label="Match rate"
              tip="How often customer messages were matched to your knowledge base and answered by the assistant."
            />
            <CircularScore
              value={matchRate}
              label="Matched Answers"
              trend={<DeltaNote delta={matchRateDelta} compact iconOnly tooltipContent={deltaTooltip} />}
              accentId="dashboard-analytics-match-rate"
              startColor={themeColor}
              endColor="#93c5fd"
            />
          </div>
          <div className="theme-analytics-story-side-panel theme-analytics-story-side-panel-compact theme-analytics-story-side-panel-score">
            <StoryInfoLabel
              label="Helpful feedback"
              tip="How often customers marked the assistant's answers as helpful."
            />
            <CircularScore
              value={feedbackScore}
              label="Positive Ratings"
              trend={<DeltaNote delta={feedbackScoreDelta} compact iconOnly tooltipContent={deltaTooltip} />}
              accentId="dashboard-analytics-feedback-score"
              startColor={themeColor}
              endColor="#c4b5fd"
            />
          </div>
          <div className="theme-analytics-story-side-panel theme-analytics-story-side-panel-compact theme-analytics-story-side-panel-score">
            <StoryInfoLabel
              label="Suggestion engagement"
              tip="How often visitors clicked a suggestion chip after the assistant showed quick suggestions in a reply."
            />
            <div className="theme-analytics-line-score">
              <div className="theme-analytics-line-score-value">
                <strong>
                  {suggestionEngagementRate != null ? `${suggestionEngagementRate}%` : "—"} 
                </strong>  
              </div>
              {suggestionEngagementRate != null ? (
                <div className="theme-analytics-progress-track theme-analytics-progress-track-slim">
                  <div
                    className={`theme-analytics-progress-fill ${
                      suggestionEngagementRate >= 65
                        ? "theme-analytics-progress-fill-success"
                        : suggestionEngagementRate >= 35
                          ? "theme-analytics-progress-fill-warning"
                          : "theme-analytics-progress-fill-danger"
                    }`}
                    style={{ height: "100%", width: `${suggestionEngagementRate}%` }}
                  />
                </div>
              ) : (
                <span className="theme-analytics-story-stack-subline">
                  Available after your chatbot shows quick suggestions to visitors.
                </span>
              )}
              <span>Suggestion Click Rate</span>
            </div>
          </div>
        </div>
      </div>
      <div className="theme-analytics-story-body">
        <span className="theme-analytics-tier-pill">Assistant Performance</span>
        <h3>Track what the chatbot is getting right</h3>
        <p>
          Helpful feedback, answer match rate, and suggestion engagement in one summary view.
          Use the knowledge gap section below to review unresolved questions.
        </p>
      </div>
    </div>
  );
};

const GapStoryCard = ({
  kbGaps,
  topTopics,
  queryLog,
  queryLogLoading,
  showFullQueryLog,
  onToggleFullQueryLog,
  knowledgeGapDelta,
  topClickedSuggestions,
  analyticsPeriod,
}: {
  kbGaps: KbGap[];
  topTopics: AnalyticsData["topTopics"];
  queryLog: QueryLogItem[];
  queryLogLoading: boolean;
  showFullQueryLog: boolean;
  onToggleFullQueryLog: () => void;
  knowledgeGapDelta?: number | null;
  topClickedSuggestions?: AnalyticsData["topClickedSuggestions"];
  analyticsPeriod: string;
}) => {
  const deltaTooltip = getDeltaTooltipLabel(analyticsPeriod);
  const unansweredCount = kbGaps.length;
  const matchedTopicCount = topTopics?.length ?? 0;
  const groupedQueryCount = queryLog.length;
  const topGapHits = kbGaps[0]?.hitCount ?? kbGaps[0]?.hits ?? 0;
  const rankedSuggestions = (topClickedSuggestions || [])
    .filter((item: AnalyticsSuggestionPoint | undefined) => item && toSafeNumber(item.clicks) > 0)
    .slice(0, 4);

  return (
    <div className="theme-analytics-story-card theme-analytics-story-card-gaps theme-analytics-story-card-full">
      <div className="theme-analytics-story-visual">
        <div className="theme-analytics-story-head theme-analytics-story-head-coverage">
          <div>
            <span className="theme-analytics-story-big">{unansweredCount}</span>
            <StoryInfoLabel
              label="open knowledge gaps"
              tip="Unique unanswered customer questions that your knowledge base did not cover in the selected period."
              subdued
            />
          </div>
          <div className="theme-analytics-coverage-chip-row">
            <span className="theme-analytics-coverage-chip theme-analytics-coverage-chip-danger">
              {unansweredCount} open
            </span>
            <DeltaBadge delta={knowledgeGapDelta ?? null} favorableDirection="down" tooltipContent={deltaTooltip} />
            <span className="theme-analytics-coverage-chip theme-analytics-coverage-chip-success">
              {matchedTopicCount} matched topics
            </span>
            <span className="theme-analytics-coverage-chip">
              {groupedQueryCount} grouped queries
            </span>
          </div>
        </div>
        <div className="theme-analytics-gap-panels">
          <div className="theme-analytics-gap-panel theme-analytics-gap-panel-flagged">
            <InlineStack align="space-between" blockAlign="center">
              <StoryInfoLabel
                label="Top unanswered"
                tip="The most frequent questions the assistant could not answer, prioritized by how often customers asked them."
              />
              {topGapHits > 0 && <span className="theme-analytics-panel-badge">{topGapHits}x hottest</span>}
            </InlineStack>
            <div className="theme-analytics-gap-list">
              {kbGaps.slice(0, 4).map((gap, index) => (
                <div key={gap.id || index} className="theme-analytics-gap-item">
                  <div className="theme-analytics-gap-copy">
                    <span className="theme-analytics-gap-icon">!</span>
                    <span className="theme-analytics-gap-question">{gap.question}</span>
                  </div>
                  <span className="theme-analytics-gap-hit-pill">{gap.hitCount ?? gap.hits ?? 0}x</span>
                </div>
              ))}
              {kbGaps.length === 0 && (
                <div className="theme-analytics-gap-empty">No unanswered questions in this period.</div>
              )}
            </div>
          </div>
          <div className="theme-analytics-gap-panel theme-analytics-gap-panel-covered">
            <InlineStack align="space-between" blockAlign="center">
              <StoryInfoLabel
                label="Top matched topics"
                tip="Knowledge base topics that successfully answered the most customer questions in this period."
              />
              {matchedTopicCount > 0 && <span className="theme-analytics-panel-badge theme-analytics-panel-badge-success">{matchedTopicCount} topics</span>}
            </InlineStack>
            <div className="theme-analytics-story-bars">
              {(topTopics || []).slice(0, 4).map((topic: AnalyticsTopicPoint, index: number) => (
                <div key={topic.kbId || index} className="theme-analytics-story-bar-row">
                  <span className="theme-analytics-story-bar-label">{topic.title}</span>
                  <div className="theme-analytics-story-bar-track">
                    <div
                      className="theme-analytics-story-bar-fill"
                      style={{ width: `${Math.max(12, Math.min(100, topic.matchCount * 8))}%`, background: "linear-gradient(90deg, #16a34a, #22c55e)" }}
                    />
                  </div>
                  <span className="theme-analytics-story-bar-value">{topic.matchCount}x</span>
                </div>
              ))}
              {(!topTopics || topTopics.length === 0) && (
                <div className="theme-analytics-gap-empty">Matched topics will appear after more answered chats.</div>
              )}
            </div>
          </div>
          <div className="theme-analytics-gap-panel theme-analytics-gap-panel-suggestions">
            <InlineStack align="space-between" blockAlign="center">
              <StoryInfoLabel
                label="Top clicked suggestions"
                tip="The suggested prompts or actions visitors clicked most often in chatbot replies during this period."
              />
              {rankedSuggestions.length > 0 && <span className="theme-analytics-panel-badge">{rankedSuggestions.length} ranked</span>}
            </InlineStack>
            <div className="theme-analytics-gap-list">
              {rankedSuggestions.map((item: AnalyticsSuggestionPoint, index: number) => (
                <div key={`${item.text}-${index}`} className="theme-analytics-gap-item theme-analytics-gap-item-info">
                  <div className="theme-analytics-gap-copy">
                    <span className="theme-analytics-gap-icon theme-analytics-gap-icon-info">?</span>
                    <span className="theme-analytics-gap-question">{item.text}</span>
                  </div>
                  <span className="theme-analytics-gap-hit-pill theme-analytics-gap-hit-pill-info">{item.clicks}x</span>
                </div>
              ))}
              {rankedSuggestions.length === 0 && (
                <div className="theme-analytics-gap-empty">Top clicked suggestions will appear after visitors start using quick replies.</div>
              )}
            </div>
          </div>
          <div className={`theme-analytics-gap-panel theme-analytics-gap-panel-evidence${showFullQueryLog ? " theme-analytics-gap-panel-evidence-active" : ""}`}>
            <InlineStack align="space-between" blockAlign="center">
              <div className="theme-analytics-query-panel-head">
                <StoryInfoLabel
                  label="Grouped query evidence"
                  tip="Raw customer phrasing grouped by repeat frequency, match status, and recency so you can spot patterns quickly."
                />
              </div>
              <button type="button" className="theme-analytics-inline-toggle" onClick={onToggleFullQueryLog}>
                {showFullQueryLog ? "Hide grouped log" : "Open grouped log"}
              </button>
            </InlineStack>
            <div className="theme-analytics-query-preview-list">
              {queryLog.slice(0, 4).map((row, index) => (
                <div key={`${row.queryText}-${index}`} className="theme-analytics-query-preview-row">
                  <div className="theme-analytics-query-preview-copy">
                    <span className="theme-analytics-query-preview-text" title={row.queryText}>{row.queryText}</span>
                    <div className="theme-analytics-query-preview-meta">
                      <span className={`theme-query-status-pill ${row.isMatched ? "theme-query-status-pill-matched" : "theme-query-status-pill-unmatched"}`}>
                        {row.isMatched ? "matched" : "unmatched"}
                      </span>
                      <span className="theme-analytics-query-preview-date">
                        {row.lastAsked ? new Date(row.lastAsked).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                      </span>
                    </div>
                  </div>
                  <span className="theme-analytics-query-preview-hits">{row.hits}x</span>
                </div>
              ))}
              {queryLogLoading && (
                <div className="theme-analytics-gap-empty">Loading grouped query activity…</div>
              )}
              {!queryLogLoading && queryLog.length === 0 && (
                <div className="theme-analytics-gap-empty">Grouped query activity will appear after chatbot conversations start.</div>
              )}
            </div>
            {!queryLogLoading && queryLog.length > 4 && !showFullQueryLog && (
              <div className="theme-analytics-query-preview-footnote">Showing 4 of {queryLog.length} grouped queries.</div>
            )}
            {!queryLogLoading && queryLog.length > 50 && showFullQueryLog && (
              <div className="theme-analytics-query-preview-footnote">Showing top 50 of {queryLog.length} grouped queries.</div>
            )}
          </div>
          {showFullQueryLog && (
            <div className="theme-analytics-expanded-log">
              <div className="theme-analytics-expanded-log-shell">
                <InlineStack align="space-between" blockAlign="start">
                  <BlockStack gap="050">
                    <Text as="h3" variant="headingSm">
                      Grouped Query Log
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Every unique customer question grouped by phrasing, matched result, and recency.
                    </Text>
                  </BlockStack>
                  <span className="theme-analytics-panel-badge">{groupedQueryCount} groups</span>
                </InlineStack>
                {queryLogLoading ? (
                  <div className="theme-analytics-gap-empty">Loading grouped query activity…</div>
                ) : queryLog.length === 0 ? (
                  <div className="theme-analytics-gap-empty">Grouped query activity will appear after chatbot conversations start.</div>
                ) : (
                  <div className="theme-analytics-expanded-log-scroll">
                    <div className="theme-query-table-wrap">
                      <table className="theme-query-table">
                        <thead>
                          <tr className="theme-query-table-head-row">
                            <th className="theme-query-table-head-cell theme-query-cell-start">Query</th>
                            <th className="theme-query-table-head-cell">KB Match</th>
                            <th className="theme-query-table-head-cell theme-query-cell-center">Status</th>
                            <th className="theme-query-table-head-cell theme-query-cell-end">Hits</th>
                            <th className="theme-query-table-head-cell theme-query-cell-end theme-query-cell-last">Last Asked</th>
                          </tr>
                        </thead>
                        <tbody>
                          {queryLog.slice(0, 50).map((row, index) => (
                            <tr key={`${row.queryText}-${index}`} className={index < Math.min(queryLog.length, 50) - 1 ? "theme-query-table-row" : undefined}>
                              <td className="theme-query-cell-start theme-query-cell-query" title={row.queryText}>
                                {row.queryText}
                              </td>
                              <td className="theme-query-table-kb theme-query-cell-kb" title={row.kbTitle || "—"}>
                                {row.kbTitle || <span className="theme-query-table-empty">—</span>}
                              </td>
                              <td className="theme-query-cell-center">
                                <span className={`theme-query-status-pill ${row.isMatched ? "theme-query-status-pill-matched" : "theme-query-status-pill-unmatched"}`}>
                                  {row.isMatched ? "matched" : "unmatched"}
                                </span>
                              </td>
                              <td className="theme-query-cell-end theme-query-cell-hits">{row.hits}</td>
                              <td className="theme-query-table-last theme-query-cell-end theme-query-cell-last theme-query-cell-date">
                                {row.lastAsked ? new Date(row.lastAsked).toLocaleDateString() : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="theme-analytics-story-body">
        <span className="theme-analytics-tier-pill">Knowledge Coverage</span>
        <h3>See what customers ask, what Reach answers, and what still slips through</h3>
        <p>
          This coverage desk keeps the misses, the working topics, and the raw customer phrasing together so you can
          update your knowledge base without context switching.
        </p>
      </div>
    </div>
  );
};

const DataManagementCard = ({ onWipe }: { onWipe: () => void }) => (
  <div className="theme-analytics-side-panel theme-analytics-side-panel-danger">
    <BlockStack gap="200">
      <Text as="h3" variant="headingSm">
        Data Management
      </Text>
      <Text as="p" variant="bodySm" tone="subdued">
        Need to start fresh? Permanently wipe analytics data for this store, including visitor counts, interaction logs, and engagement history.
      </Text>
      <button
        type="button"
        onClick={onWipe}
        className="theme-analytics-danger-btn"
      >
        Wipe Analytics Data
      </button>
    </BlockStack>
  </div>
);

type AnalyticsTabProps = {
  analyticsData: AnalyticsData | null;
  analyticsLoading: boolean;
  analyticsPeriod: string;
  chatbotSectionOpen: boolean;
  isAgentPlus: boolean;
  isFree: boolean;
  isRefreshing: boolean;
  isRepPlus: boolean;
  kbGaps: KbGap[];
  onOpenAppearanceTab: () => void;
  onOpenPlansTab: () => void;
  onRefresh: () => void | Promise<void>;
  onShowAnalyticsWipeModal: () => void;
  queryLog: QueryLogItem[];
  queryLogLoading: boolean;
  setAnalyticsPeriod: (value: string) => void;
  setChatbotSectionOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setVisitorSectionOpen: React.Dispatch<React.SetStateAction<boolean>>;
  themeColor: string;
  visitorSectionOpen: boolean;
};

export function AnalyticsTab({
  analyticsData,
  analyticsLoading,
  analyticsPeriod,
  chatbotSectionOpen,
  isAgentPlus,
  isFree,
  isRefreshing,
  isRepPlus,
  kbGaps,
  onOpenAppearanceTab,
  onOpenPlansTab,
  onRefresh,
  onShowAnalyticsWipeModal,
  queryLog,
  queryLogLoading,
  setAnalyticsPeriod,
  setChatbotSectionOpen,
  setVisitorSectionOpen,
  themeColor,
  visitorSectionOpen,
}: AnalyticsTabProps) {
  const [showFullQueryLog, setShowFullQueryLog] = React.useState(false);

  return (
    <BlockStack gap="400">
      <style>{`
        .theme-analytics-shell {
          --analytics-accent: #2563eb;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        .theme-analytics-hero-card {
          position: relative;
          overflow: hidden;
          border-radius: 24px;
          border: 1px solid rgba(148, 163, 184, 0.18);
          background:
            radial-gradient(circle at top right, color-mix(in srgb, var(--analytics-accent) 18%, transparent), transparent 34%),
            radial-gradient(circle at bottom left, color-mix(in srgb, var(--analytics-accent) 12%, white), transparent 28%),
            linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%);
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.85);
        }
        .theme-analytics-hero-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.5fr) minmax(260px, 0.9fr);
          gap: 1.25rem;
          align-items: start;
        }
        .theme-analytics-title-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .theme-analytics-title-period {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.24rem 0.68rem;
          border-radius: 999px;
          border: 1px solid rgba(14, 165, 233, 0.18);
          background: color-mix(in srgb, var(--analytics-accent) 8%, white);
          color: color-mix(in srgb, var(--analytics-accent) 72%, #0f172a);
          font-size: 0.78rem;
          font-weight: 800;
          white-space: nowrap;
        }
        .theme-analytics-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .theme-analytics-stat-chip {
          display: inline-flex;
          flex-direction: column;
          gap: 2px;
          min-width: 124px;
          padding: 12px 14px;
          border-radius: 16px;
          background: rgba(255,255,255,0.72);
          border: 1px solid rgba(226, 232, 240, 0.95);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.85);
        }
        .theme-analytics-stat-chip-label {
          font-size: 11px;
          line-height: 1.2;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #64748b;
          font-weight: 700;
        }
        .theme-analytics-stat-chip-value {
          font-size: 18px;
          line-height: 1.1;
          color: #0f172a;
          font-weight: 800;
          letter-spacing: -0.03em;
        }
        .theme-analytics-side-panel {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 16px;
          border-radius: 18px;
          background: rgba(255,255,255,0.78);
          border: 1px solid rgba(226, 232, 240, 0.95);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-analytics-side-panel-stack {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          align-items: stretch;
        }
        .theme-analytics-side-panel-danger {
          border-color: rgba(252, 165, 165, 0.7);
          background: linear-gradient(180deg, rgba(254,242,242,0.98) 0%, rgba(255,255,255,0.98) 100%);
        }
        .theme-analytics-danger-btn {
          border: none;
          border-radius: 12px;
          padding: 10px 14px;
          background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          box-shadow: 0 10px 20px rgba(185, 28, 28, 0.2);
        }
        .theme-analytics-visual-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1.25rem;
        }
        .theme-analytics-story-card {
          position: relative;
          overflow: hidden;
          border-radius: 24px;
          border: 1px solid rgba(226, 232, 240, 0.95);
          background: linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.98) 100%);
          box-shadow: 0 20px 46px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-analytics-story-card::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 3px;
          background: linear-gradient(90deg, var(--analytics-accent), color-mix(in srgb, var(--analytics-accent) 42%, white));
          opacity: 0.8;
          z-index: 2;
          pointer-events: none;
        }
        .theme-analytics-story-card-gaps::before {
          background: linear-gradient(90deg, rgba(220,38,38,0.78), rgba(251,146,60,0.46));
        }
        .theme-analytics-story-card-gaps .theme-analytics-story-visual {
          background:
            radial-gradient(circle at top left, rgba(220,38,38,0.08), transparent 22%),
            radial-gradient(circle at bottom right, rgba(14,165,233,0.08), transparent 26%),
            linear-gradient(160deg, rgba(15,23,42,0.03), rgba(255,255,255,0.72));
        }
        .theme-analytics-story-card-perf::before {
          background: linear-gradient(90deg, var(--analytics-accent), color-mix(in srgb, var(--analytics-accent) 36%, #a78bfa));
        }
        .theme-analytics-story-card-wide {
          grid-column: span 2;
        }
        .theme-analytics-story-card-full {
          grid-column: span 3;
        }
        .theme-analytics-story-visual {
          position: relative;
          z-index: 1;
          padding: 1.6rem 1.6rem 1.35rem;
          background:
            radial-gradient(circle at top right, color-mix(in srgb, var(--analytics-accent) 10%, transparent), transparent 26%),
            linear-gradient(160deg, color-mix(in srgb, var(--analytics-accent) 6%, white), rgba(248,250,252,0.92));
          border-bottom: 1px solid rgba(226, 232, 240, 0.95);
          border-radius: 24px 24px 0 0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          min-height: 14rem;
        }
        .theme-analytics-story-visual-visitor,
        .theme-analytics-story-visual-perf {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(14rem, 0.96fr) minmax(10.25rem, 0.8fr) minmax(10.25rem, 0.8fr) minmax(10rem, 0.72fr);
          align-items: stretch;
          gap: 1rem;
        }
        .theme-analytics-story-visual-perf {
          grid-template-columns:
            minmax(10.25rem, 0.86fr)
            minmax(10.25rem, 0.86fr)
            minmax(11.25rem, 0.98fr)
            minmax(10.25rem, 0.86fr)
            minmax(12.5rem, 1fr);
        }
        .theme-analytics-story-visual-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1.05rem 1.1rem;
          border-radius: 18px;
          border: 1px solid rgba(226, 232, 240, 0.95);
          background: rgba(255,255,255,0.84);
          box-shadow: 0 12px 26px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.94);
        }
        .theme-analytics-story-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
        }
        .theme-analytics-story-visual-main .theme-analytics-story-head {
          margin-bottom: 0.45rem;
        }
        .theme-analytics-inline-info {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          opacity: 0.85;
        }
        .theme-analytics-inline-label {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          width: 100%;
          font-size: 0.74rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #475569;
        }
        .theme-analytics-inline-label > span:first-child {
          width: fit-content;
        }
        .theme-analytics-inline-hint-badge {
          margin-right: auto;
          border: 1px solid rgba(14, 165, 233, 0.22);
          background: linear-gradient(135deg, rgba(14,165,233,0.12), rgba(59,130,246,0.18));
          color: #0369a1;
          border-radius: 999px;
          padding: 0.2rem 0.56rem;
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          cursor: help;
          line-height: 1.2;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.7);
        }
        .theme-analytics-inline-label-subdued {
          margin-top: 0.25rem;
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0;
          text-transform: none;
          color: #64748b;
        }
        .theme-analytics-story-head-coverage {
          align-items: center;
        }
        .theme-analytics-story-big {
          display: block;
          font-size: 2.35rem;
          font-weight: 800;
          letter-spacing: -0.05em;
          line-height: 1;
          color: #0f172a;
        }
        .theme-analytics-story-label {
          display: block;
          margin-top: 0.25rem;
          font-size: 0.8rem;
          color: #64748b;
        }
        .theme-analytics-story-trend {
          display: inline-flex;
          align-items: center;
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          background: rgba(22,163,74,0.1);
          border: 1px solid rgba(22,163,74,0.2);
          color: #15803d;
          font-size: 0.78rem;
          font-weight: 700;
          white-space: nowrap;
        }
        .theme-analytics-story-delta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.34rem;
          width: fit-content;
          padding: 0;
          border: none;
          background: transparent;
          color: #475569;
          font-size: 0.72rem;
          font-weight: 800;
          line-height: 1.25;
        }
        .theme-analytics-story-delta svg {
          width: 0.95rem;
          height: 0.95rem;
          flex-shrink: 0;
        }
        .theme-analytics-story-delta-compact {
          font-size: 0.76rem;
          white-space: nowrap;
        }
        .theme-analytics-story-delta-icon-only {
          width: 1rem;
          height: 1rem;
          padding: 0.08rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.76);
          box-shadow: 0 1px 3px rgba(15,23,42,0.12);
          vertical-align: middle;
        }
        .theme-analytics-story-delta-icon-only svg {
          width: 0.78rem;
          height: 0.78rem;
        }
        .theme-analytics-story-delta-positive {
          color: #166534;
        }
        .theme-analytics-story-delta-negative {
          color: #dc2626;
        }
        .theme-analytics-story-delta-neutral {
          color: #475569;
        }
        .theme-analytics-story-columns {
          display: flex;
          align-items: stretch;
          gap: 0.45rem;
          height: 8.8rem;
          flex: 1;
        }
        .theme-analytics-story-columns-shell {
          position: relative;
        }
        .theme-analytics-story-column {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          height: 100%;
          gap: 0.35rem;
        }
        .theme-analytics-story-column-plot {
          flex: 1;
          width: 100%;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          min-height: 0;
        }
        .theme-analytics-story-column-fill {
          width: 100%;
          min-height: 8px;
          border-radius: 4px 4px 0 0;
          background: linear-gradient(180deg, #38bdf8, #1e3a8a);
          opacity: 0.72;
          position: relative;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 0.34rem;
          overflow: visible;
        }
        .theme-analytics-story-column-value {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.22rem;
          font-size: 0.72rem;
          line-height: 1;
          font-weight: 800;
          color: rgba(255,255,255,0.96);
          text-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
        }
        .theme-analytics-story-column-value-outside {
          position: absolute;
          top: -1.12rem;
          color: var(--analytics-accent);
          text-shadow: none;
        }
        .theme-analytics-story-column-label {
          font-size: 0.68rem;
          font-weight: 700;
          color: #64748b;
        }
        .theme-analytics-story-column-label-row {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.24rem;
          flex-wrap: nowrap;
          text-align: center;
          white-space: nowrap;
        }
        .theme-analytics-story-side-panel {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 1.75rem;
          justify-content: center;
          padding: 1rem;
          border-radius: 18px;
          border: 1px solid rgba(226, 232, 240, 0.95);
          background: rgba(255,255,255,0.88);
          box-shadow: 0 12px 26px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.94);
        }
        .theme-analytics-story-visual-perf > .theme-analytics-story-side-panel:first-child {
          padding: 1rem;
          border: 1px solid rgba(226, 232, 240, 0.95);
        }
        .theme-analytics-story-side-panel-compact {
          align-items: center;
          text-align: center;
        }
        .theme-analytics-story-side-panel-score {
          align-items: center;
        }
        .theme-analytics-story-side-panel-top {
          justify-content: flex-start;
          align-self: start;
          min-height: 100%;
        }
        .theme-analytics-story-visual-visitor {
          display: flex;
          flex-direction: column;
        }
        .theme-analytics-visitor-row {
          display: grid;
          gap: 1rem;
          align-items: stretch;
        }
        .theme-analytics-visitor-row-primary {
          grid-template-columns: minmax(0, 1fr) minmax(24rem, 1.25fr);
        }
        .theme-analytics-visitor-row-secondary {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.12fr) minmax(0, 0.9fr);
        }
        .theme-analytics-visitor-row .theme-analytics-story-visual-main,
        .theme-analytics-visitor-row .theme-analytics-story-side-panel {
          min-height: 100%;
        }
        .theme-analytics-story-visual-perf {
          display: flex;
          flex-direction: column;
        }
        .theme-analytics-perf-score-row {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
          align-items: stretch;
        }
        .theme-analytics-perf-score-row .theme-analytics-story-side-panel {
          min-height: 100%;
        }
        .theme-analytics-story-side-total {
          font-size: 2rem;
          line-height: 1;
          font-weight: 800;
          letter-spacing: -0.05em;
          color: #0f172a;
        }
        .theme-analytics-story-side-total-note {
          font-size: 0.78rem;
          line-height: 1.45;
          color: #64748b;
        }
        .theme-analytics-story-side-title {
          font-size: 0.74rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #64748b;
          margin: 0 0 0.15rem;
        }
        .theme-analytics-story-bars {
          display: flex;
          flex-direction: column;
          gap: 0.58rem;
        }
        .theme-analytics-story-bar-row {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          min-width: 0;
        }
        .theme-analytics-story-bar-label {
          font-size: 0.8rem;
          color: #475569;
          width: 8rem;
          flex: 0 0 8rem;
        }
        .theme-analytics-story-bar-track {
          flex: 1;
          height: 0.42rem;
          border-radius: 999px;
          background: rgba(15,23,42,0.08);
          overflow: hidden;
        }
        .theme-analytics-story-bar-fill {
          height: 100%;
          border-radius: 999px;
        }
        .theme-analytics-story-bar-value {
          font-size: 0.76rem;
          width: 3.5rem;
          flex: 0 0 3.5rem;
          text-align: right;
          color: #64748b;
          font-weight: 700;
        }
        .theme-analytics-ring-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          flex-shrink: 0;
          min-width: 7rem;
        }
        .theme-analytics-ring-overlay-wrap {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
        .theme-analytics-ring-overlay-delta {
          position: absolute;
          top: 0.3rem;
          left: calc(50% + 2.55rem);
          transform: translateY(-10%);
          pointer-events: auto;
        }
        .theme-analytics-ring {
          position: relative;
          width: 6rem;
          height: 6rem;
          border-radius: 999px;
          background: rgba(248,250,252,0.72);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.95);
        }
        .theme-analytics-ring svg {
          width: 100%;
          height: 100%;
        }
        .theme-analytics-ring span {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.08rem;
          font-weight: 800;
          color: #0f172a;
        }
        .theme-analytics-ring-label {
          margin: 0;
          max-width: 9rem;
          text-align: center;
          font-size: 0.74rem;
          color: #64748b;
          line-height: 1.3;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.28rem;
          flex-wrap: nowrap;
          white-space: nowrap;
        }
        .theme-analytics-ring-label > span {
          white-space: nowrap;
        }
        .theme-analytics-story-stack-stats {
          gap: 0.95rem;
        }
        .theme-analytics-story-side-panel-activity {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 0.9rem;
          align-items: stretch;
          justify-content: stretch;
        }
        .theme-analytics-story-side-panel-activity > .theme-analytics-inline-label {
          grid-column: 1 / -1;
        }
        .theme-analytics-story-side-panel-activity .theme-analytics-story-stack-stat {
          min-height: 6.7rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .theme-analytics-story-side-panel-activity .theme-analytics-story-stack-stat:not(.theme-analytics-story-stack-stat-progress) strong {
          font-size: 1.48rem;
        }
        .theme-analytics-story-side-panel-score > .theme-analytics-story-delta {
          align-self: center;
        }
        .theme-analytics-line-score-value {
          width: 78%;
          display: flex;
          align-items: center;
          justify-content: end;
          text-align: end;
        }
        .theme-analytics-line-score {
          width: 100%;
          min-height: 7.7rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          text-align: center;
        }
        .theme-analytics-line-score strong {
          font-size: 2.2rem;
          line-height: 1;
          font-weight: 850;
          letter-spacing: -0.06em;
          color: #0f172a;
        }
        .theme-analytics-line-score > span:last-child {
          font-size: 0.74rem;
          line-height: 1.3;
          color: #64748b;
          font-weight: 700;
        }
        .theme-analytics-line-score .theme-analytics-progress-track {
          width: min(100%, 20rem);
        }
        .theme-analytics-story-stack-stat {
          padding: 0.82rem 0.9rem;
          border-radius: 14px;
          border: 1px solid rgba(226, 232, 240, 0.95);
          background: rgba(248,250,252,0.86);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-analytics-story-stack-stat strong {
          display: block;
          font-size: 1.2rem;
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #0f172a;
        }
        .theme-analytics-story-stack-stat span {
          font-size: 0.76rem;
          line-height: 1.3;
          color: #64748b;
        }
        .theme-analytics-story-stack-subline {
          display: block;
          margin-top: 0.2rem;
          font-size: 0.72rem;
          line-height: 1.45;
          color: #64748b;
        }
        .theme-analytics-story-stack-stat-progress {
          gap: 0.65rem;
          padding: 1rem 1.05rem;
        }
        .theme-analytics-story-stack-stat-progress .theme-analytics-inline-label {
          font-size: 0.72rem;
          font-weight: 850;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #334155;
        }
        .theme-analytics-story-stack-progress-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 0.4rem;
        }
        .theme-analytics-story-stack-progress-head strong {
          flex-shrink: 0;
          font-size: 1.9rem;
        }
        .theme-analytics-story-mini-note {
          font-size: 0.78rem;
          color: #64748b;
          font-weight: 700;
        }
        .theme-analytics-donut-block {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
          padding-top: 0.2rem;
        }
        .theme-analytics-donut-block-large {
          padding-top: 0.4rem;
        }
        .theme-analytics-donut {
          position: relative;
          width: 6.2rem;
          height: 6.2rem;
          border-radius: 999px;
          background: rgba(248,250,252,0.72);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.92), 0 8px 20px rgba(15,23,42,0.06);
        }
        .theme-analytics-donut-large {
          width: 6.9rem;
          height: 6.9rem;
        }
        .theme-analytics-donut-hover-tip {
          position: absolute;
          left: 0;
          top: 0;
          transform: translate(-50%, calc(-100% - 10px));
          z-index: 3;
          padding: 0.38rem 0.55rem;
          border-radius: 10px;
          background: #111827;
          color: #fff;
          font-size: 0.72rem;
          font-weight: 600;
          line-height: 1.25;
          white-space: nowrap;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.2);
          pointer-events: none;
          transition: left 40ms linear, top 40ms linear;
        }
        .theme-analytics-donut-hover-tip::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: -5px;
          width: 10px;
          height: 10px;
          background: #111827;
          transform: translateX(-50%) rotate(45deg);
          border-radius: 2px;
        }
        .theme-analytics-donut-svg {
          width: 100%;
          height: 100%;
          overflow: visible;
        }
        .theme-analytics-donut-segment {
          transition: opacity 0.18s ease, transform 0.18s ease;
        }
        .theme-analytics-donut-segment-clickable {
          cursor: pointer;
        }
        .theme-analytics-donut-core {
          position: absolute;
          inset: 0.92rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.96);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.92);
        }
        .theme-analytics-donut-core strong {
          font-size: 1.05rem;
          line-height: 1;
          font-weight: 800;
          color: #0f172a;
        }
        .theme-analytics-donut-core span {
          margin-top: 0.18rem;
          font-size: 0.62rem;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          line-height: 1.1;
        }
        .theme-analytics-donut-legend {
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          gap: 0.3rem;
          justify-content: center;
          margin-top: 0.2rem;
        }
        .theme-analytics-donut-legend-row {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.78rem;
          color: #475569;
          border: none;
          background: transparent;
          padding: 0.2rem 0.3rem;
          margin: 0;
          text-align: left;
          border-radius: 10px;
        }
        .theme-analytics-donut-legend-row-clickable {
          cursor: pointer;
        }
        .theme-analytics-donut-legend-row-active {
          background: rgba(241, 245, 249, 0.92);
        }
        .theme-analytics-donut-legend-swatch {
          width: 0.72rem;
          height: 0.72rem;
          border-radius: 999px;
          flex-shrink: 0;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.72);
        }
        .theme-analytics-donut-legend-copy strong {
          color: #0f172a;
        }
        .theme-analytics-segmented-wrap {
          position: relative;
          width: 100%;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding: 0.4rem 1.65rem 0;
        }
        .theme-analytics-segmented-track {
          display: flex;
          width: 100%;
          min-height: 3.1rem;
          overflow: visible;
          border-radius: 999px;
          background: rgba(226, 232, 240, 0.92);
          border: 1px solid rgba(226, 232, 240, 0.96);
          box-shadow: inset 0 1px 2px rgba(15,23,42,0.08), 0 10px 20px rgba(15,23,42,0.05);
          position: relative;
          isolation: isolate;
        }
        .theme-analytics-segmented-slice {
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-size: 0.9rem;
          font-weight: 850;
          letter-spacing: -0.02em;
          text-shadow: 0 1px 3px rgba(15, 23, 42, 0.22);
          cursor: help;
          transition: filter 0.16s ease, transform 0.16s ease;
          position: relative;
        }
        .theme-analytics-segmented-slice:hover {
          filter: saturate(1.08) brightness(1.02);
        }
        .theme-analytics-segmented-slice:first-child {
          border-radius: 999px 0 0 999px;
        }
        .theme-analytics-segmented-slice:last-child {
          border-radius: 0 999px 999px 0;
        }
        .theme-analytics-segmented-slice + .theme-analytics-segmented-slice {
          box-shadow: inset 1px 0 0 rgba(255,255,255,0.5);
        }
        .theme-analytics-segmented-slice-zero:first-child + .theme-analytics-segmented-slice {
          border-radius: 999px;
          box-shadow: none;
        }
        .theme-analytics-segmented-slice:has(+ .theme-analytics-segmented-slice-zero:last-child) {
          border-radius: 999px;
        }
        .theme-analytics-segmented-slice-tiny span {
          position: absolute;
          left: 100%;
          margin-left: 0.188rem;
          min-width: 2rem;
          padding: 0.16rem 0.38rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.96);
          box-shadow: 0 5px 12px rgba(15, 23, 42, 0.12);
          text-shadow: none;
          z-index: 2;
        }
        .theme-analytics-segmented-slice-zero {
          overflow: visible;
          z-index: 2;
        }
        .theme-analytics-segmented-slice-zero:first-child span {
          position: absolute;
          left: 0.35rem;
          top: 50%;
          transform: translateY(-50%);
          min-width: 1.6rem;
          padding: 0.16rem 0.34rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.96);
          box-shadow: 0 5px 12px rgba(15, 23, 42, 0.12);
          text-shadow: none;
          z-index: 3;
        }
        .theme-analytics-segmented-slice-zero:last-child span {
          position: absolute;
          right: 0.35rem;
          top: 50%;
          transform: translateY(-50%);
          min-width: 1.6rem;
          padding: 0.16rem 0.34rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.96);
          box-shadow: 0 5px 12px rgba(15, 23, 42, 0.12);
          text-shadow: none;
          z-index: 3;
        }
        .theme-analytics-segmented-legend {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.45rem 0.7rem;
          color: #475569;
          font-size: 0.78rem;
          font-weight: 700;
        }
        .theme-analytics-segmented-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
        }
        .theme-analytics-nested-track {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          width: 100%;
          margin-top: 0.5rem;
        }
        .theme-analytics-nested-header {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.42rem;
          margin-bottom: -0.5rem;
        }
        .theme-analytics-nested-pill {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.65rem;
          border-radius: 999px;
          background: color-mix(in srgb, var(--analytics-accent) 12%, white);
          color: var(--analytics-accent);
          font-size: 0.72rem;
          font-weight: 850;
          line-height: 1.2;
        }
        .theme-analytics-nested-bar {
          position: relative;
          height: 2.5rem;
          overflow: hidden;
          border: 1px solid rgba(148, 163, 184, 0.28);
          border-radius: 999px;
          background: rgba(248, 250, 252, 0.92);
          box-shadow: inset 0 2px 4px rgba(15, 23, 42, 0.03);
        }
        .theme-analytics-nested-fill {
          position: absolute;
          inset: 0 auto 0 0;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          min-width: 0;
          padding-right: 0.85rem;
          border-radius: 999px;
          background: linear-gradient(90deg, color-mix(in srgb, var(--analytics-accent) 92%, white), #38bdf8);
          box-shadow: inset -2px 0 4px rgba(255,255,255,0.22);
          transition: width 1s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .theme-analytics-nested-fill-label {
          color: #ffffff;
          font-size: 0.95rem;
          font-weight: 850;
          line-height: 1;
          text-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
        }
        .theme-analytics-nested-stats {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.75rem;
        }
        .theme-analytics-nested-stat {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          padding: 0.85rem 1rem;
          border: 1px solid rgba(148, 163, 184, 0.28);
          border-radius: 8px;
          background: rgba(248, 250, 252, 0.86);
        }
        .theme-analytics-nested-stat-highlight {
          border-color: color-mix(in srgb, var(--analytics-accent) 18%, transparent);
          background: color-mix(in srgb, var(--analytics-accent) 5%, white);
        }
        .theme-analytics-nested-stat span {
          color: #64748b;
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.04em;
          line-height: 1.2;
          text-transform: uppercase;
        }
        .theme-analytics-nested-stat-highlight span {
          color: var(--analytics-accent);
        }
        .theme-analytics-nested-stat strong {
          color: #0f172a;
          font-size: 1.25rem;
          line-height: 1;
          font-weight: 850;
          letter-spacing: -0.04em;
        }
        .theme-analytics-nested-stat-highlight strong {
          color: var(--analytics-accent);
        }
        .theme-analytics-funnel {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          padding: 0.15rem 0.2rem 0.25rem;
        }
        .theme-analytics-funnel-kpi {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.42rem;
          width: 100%;
          color: #0f172a;
          padding-right: 0.45rem;
        }
        .theme-analytics-funnel-kpi strong {
          font-size: 1.85rem;
          line-height: 1;
          font-weight: 850;
          letter-spacing: -0.05em;
        }
        .theme-analytics-funnel-shape {
          position: relative;
          width: 100%;
          max-width: 22rem;
          min-height: 8.25rem;
          color: #ffffff;
          filter: drop-shadow(0 16px 22px rgba(15, 23, 42, 0.13));
          align-self: center;
        }
        .theme-analytics-funnel-shape svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
        }
        .theme-analytics-funnel-stage {
          position: absolute;
          left: 50%;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0;
          border-radius: 0;
          color: #ffffff;
          box-shadow: none;
          transform: translateX(-50%);
          pointer-events: none;
        }
        .theme-analytics-funnel-stage span {
          font-size: 0.74rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: rgba(255,255,255,0.84);
        }
        .theme-analytics-funnel-stage strong {
          font-size: 1.35rem;
          line-height: 1;
          font-weight: 850;
          letter-spacing: -0.04em;
        }
        .theme-analytics-funnel-stage-wide {
          top: 1.1rem;
          width: calc(100% - 3.6rem);
        }
        .theme-analytics-funnel-stage-narrow {
          bottom: 2.6rem;
          width: calc(100% - 10.5rem);
        }
        .theme-analytics-funnel-connector {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          min-height: 1.35rem;
          color: #475569;
          font-size: 0.76rem;
          font-weight: 800;
        }
        .theme-analytics-gap-list {
          display: flex;
          flex-direction: column;
          gap: 0.58rem;
        }
        .theme-analytics-gap-panels {
          display: grid;
          grid-template-columns: 1.05fr 0.95fr 0.95fr 1.1fr;
          gap: 1rem;
          align-items: start;
        }
        .theme-analytics-gap-panel {
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          gap: 0.7rem;
          padding: 1rem;
          border-radius: 18px;
          border: 1px solid rgba(226,232,240,0.95);
          background: rgba(255,255,255,0.72);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04), inset 0 1px 0 rgba(255,255,255,0.9);
          transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
        }
        .theme-analytics-gap-panel::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 3px;
          opacity: 0.8;
        }
        .theme-analytics-gap-panel:hover {
          transform: translateY(-1px);
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.07), inset 0 1px 0 rgba(255,255,255,0.92);
        }
        .theme-analytics-gap-panel-flagged {
          background: linear-gradient(180deg, rgba(254,242,242,0.75) 0%, rgba(255,255,255,0.85) 100%);
          border-color: rgba(248, 113, 113, 0.22);
        }
        .theme-analytics-gap-panel-flagged::before {
          background: linear-gradient(90deg, rgba(220,38,38,0.88), rgba(251,146,60,0.5));
        }
        .theme-analytics-gap-panel-covered {
          background: linear-gradient(180deg, rgba(240,253,244,0.78) 0%, rgba(255,255,255,0.85) 100%);
          border-color: rgba(74, 222, 128, 0.24);
        }
        .theme-analytics-gap-panel-covered::before {
          background: linear-gradient(90deg, rgba(22,163,74,0.82), rgba(34,197,94,0.46));
        }
        .theme-analytics-gap-panel-evidence {
          background: linear-gradient(180deg, rgba(239,246,255,0.8) 0%, rgba(255,255,255,0.85) 100%);
          border-color: rgba(56, 189, 248, 0.22);
        }
        .theme-analytics-gap-panel-evidence::before {
          background: linear-gradient(90deg, rgba(14,165,233,0.85), rgba(30,58,138,0.5));
        }
        .theme-analytics-gap-panel-suggestions {
          background: linear-gradient(180deg, rgba(245,243,255,0.82) 0%, rgba(255,255,255,0.86) 100%);
          border-color: rgba(129,140,248,0.2);
        }
        .theme-analytics-gap-panel-suggestions::before {
          background: linear-gradient(90deg, rgba(99,102,241,0.84), rgba(168,85,247,0.48));
        }
        .theme-analytics-gap-panel-evidence-active {
          box-shadow: 0 16px 32px rgba(14, 165, 233, 0.12), inset 0 1px 0 rgba(255,255,255,0.92);
          border-color: rgba(14, 165, 233, 0.28);
        }
        .theme-analytics-coverage-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: flex-end;
        }
        .theme-analytics-coverage-chip,
        .theme-analytics-panel-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: fit-content;
          padding: 0.26rem 0.62rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.86);
          border: 1px solid rgba(148,163,184,0.22);
          color: #334155;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.04em;
        }
        .theme-analytics-coverage-chip-danger {
          color: #b91c1c;
          background: rgba(254,226,226,0.82);
          border-color: rgba(239,68,68,0.2);
        }
        .theme-analytics-coverage-chip-delta-positive {
          color: #166534;
          background: rgba(220,252,231,0.82);
          border-color: rgba(34,197,94,0.18);
        }
        .theme-analytics-coverage-chip-delta-negative {
          color: #b45309;
          background: rgba(255,247,237,0.84);
          border-color: rgba(249,115,22,0.18);
        }
        .theme-analytics-coverage-chip-delta-neutral {
          color: #475569;
          background: rgba(248,250,252,0.9);
          border-color: rgba(148,163,184,0.16);
        }
        .theme-analytics-coverage-chip-success,
        .theme-analytics-panel-badge-success {
          color: #166534;
          background: rgba(220,252,231,0.82);
          border-color: rgba(34,197,94,0.18);
        }
        .theme-analytics-gap-copy {
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 0.65rem;
        }
        .theme-analytics-gap-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 0.7rem 0.8rem;
          border-radius: 14px;
          background: rgba(220,38,38,0.05);
          border: 1px solid rgba(220,38,38,0.12);
        }
        .theme-analytics-gap-item-done {
          background: rgba(22,163,74,0.05);
          border-color: rgba(22,163,74,0.14);
        }
        .theme-analytics-gap-item-info {
          background: rgba(99,102,241,0.05);
          border-color: rgba(99,102,241,0.14);
        }
        .theme-analytics-gap-icon {
          width: 1.2rem;
          height: 1.2rem;
          border-radius: 999px;
          background: rgba(220,38,38,0.14);
          color: #dc2626;
          font-size: 0.68rem;
          font-weight: 800;
          display: grid;
          place-items: center;
          flex-shrink: 0;
        }
        .theme-analytics-gap-icon-done {
          background: rgba(22,163,74,0.14);
          color: #16a34a;
        }
        .theme-analytics-gap-icon-info {
          background: rgba(99,102,241,0.14);
          color: #4f46e5;
        }
        .theme-analytics-gap-question {
          font-size: 0.8rem;
          color: #334155;
          line-height: 1.45;
        }
        .theme-analytics-gap-hit-pill {
          flex-shrink: 0;
          padding: 0.22rem 0.5rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.86);
          border: 1px solid rgba(239,68,68,0.18);
          color: #b91c1c;
          font-size: 0.72rem;
          font-weight: 800;
        }
        .theme-analytics-gap-hit-pill-info {
          border-color: rgba(99,102,241,0.18);
          color: #4338ca;
        }
        .theme-analytics-gap-empty {
          padding: 0.8rem;
          border-radius: 12px;
          background: rgba(248,250,252,0.92);
          border: 1px solid rgba(226,232,240,0.95);
          font-size: 0.8rem;
          color: #64748b;
          line-height: 1.5;
        }
        .theme-analytics-query-preview-list {
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .theme-analytics-query-preview-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.8rem;
          padding: 0.72rem 0.82rem;
          border-radius: 14px;
          background: rgba(248,250,252,0.92);
          border: 1px solid rgba(226,232,240,0.95);
          transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
        }
        .theme-analytics-query-preview-row:hover {
          transform: translateY(-1px);
          border-color: rgba(148, 163, 184, 0.32);
          box-shadow: 0 10px 20px rgba(15, 23, 42, 0.05);
        }
        .theme-analytics-query-preview-copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }
        .theme-analytics-query-preview-meta {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          flex-wrap: wrap;
        }
        .theme-analytics-query-preview-text {
          font-size: 0.8rem;
          color: #334155;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
          font-weight: 700;
        }
        .theme-analytics-query-preview-hits {
          flex-shrink: 0;
          min-width: 2.5rem;
          padding: 0.22rem 0.48rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.85);
          border: 1px solid rgba(148,163,184,0.16);
          font-size: 0.76rem;
          font-weight: 800;
          color: #334155;
          text-align: center;
        }
        .theme-analytics-query-preview-date,
        .theme-analytics-query-panel-note {
          font-size: 0.72rem;
          color: #64748b;
        }
        .theme-analytics-query-panel-head {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        .theme-analytics-query-preview-footnote {
          font-size: 0.76rem;
          color: #64748b;
        }
        .theme-analytics-expanded-log {
          margin-top: 0.75rem;
          grid-column: 1 / -1;
        }
        .theme-analytics-expanded-log-shell {
          display: flex;
          flex-direction: column;
          gap: 0.95rem;
          padding: 1.05rem;
          border-radius: 22px;
          border: 1px solid rgba(191,219,254,0.78);
          background:
            linear-gradient(180deg, rgba(239,246,255,0.78) 0%, rgba(255,255,255,0.94) 38%, rgba(248,250,252,0.95) 100%);
          box-shadow: 0 16px 36px rgba(15, 23, 42, 0.07), inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-analytics-expanded-log-scroll {
          max-height: 24rem;
          overflow: auto;
          padding-right: 0.15rem;
        }
        .theme-analytics-inline-toggle {
          border: 1px solid color-mix(in srgb, var(--analytics-accent) 28%, transparent);
          background: linear-gradient(135deg, var(--analytics-accent) 0%, color-mix(in srgb, var(--analytics-accent) 42%, white) 100%);
          color: #ffffff;
          border-radius: 999px;
          padding: 0.4rem 0.8rem;
          margin-top: 0.38rem;
          font-size: 0.74rem;
          font-weight: 800;
          cursor: pointer;
          white-space: nowrap;
          box-shadow: 0 8px 16px rgba(30,58,138,0.16);
        }
        .theme-analytics-inline-toggle:hover {
          filter: brightness(1.04);
        }
        .theme-analytics-story-body {
          position: relative;
          z-index: 1;
          padding: 1.4rem 1.6rem 1.7rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .theme-analytics-story-body h3 {
          margin: 0;
          font-size: 1.02rem;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.25;
        }
        .theme-analytics-story-body p {
          margin: 0;
          font-size: 0.9rem;
          color: #475569;
          line-height: 1.6;
        }
        .theme-analytics-tier-pill {
          display: inline-flex;
          align-items: center;
          width: fit-content;
          padding: 0.2rem 0.6rem;
          border-radius: 999px;
          background: color-mix(in srgb, var(--analytics-accent) 10%, white);
          border: 1px solid color-mix(in srgb, var(--analytics-accent) 22%, transparent);
          color: color-mix(in srgb, var(--analytics-accent) 84%, #0f172a);
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .theme-metric-card-surface {
          padding: 16px 18px;
          border-radius: 18px;
          min-height: 118px;
          background: linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.98) 100%);
          border: 1px solid rgba(226, 232, 240, 0.95);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05), inset 0 1px 0 rgba(255,255,255,0.85);
        }
        .theme-metric-card-surface[data-tone="success"] {
          background: linear-gradient(180deg, rgba(240, 253, 244, 0.98) 0%, rgba(255,255,255,0.98) 100%);
        }
        .theme-metric-card-surface[data-tone="critical"] {
          background: linear-gradient(180deg, rgba(254, 242, 242, 0.98) 0%, rgba(255,255,255,0.98) 100%);
        }
        .theme-metric-card-surface[data-tone="caution"] {
          background: linear-gradient(180deg, rgba(255, 251, 235, 0.98) 0%, rgba(255,255,255,0.98) 100%);
        }
        .theme-analytics-domain-card {
          overflow: hidden;
          border-radius: 24px;
          border: 1px solid rgba(226, 232, 240, 0.95);
          background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%);
          box-shadow: 0 18px 44px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.85);
        }
        .theme-analytics-section-toggle {
          width: 100%;
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          gap: 14px;
          align-items: center;
          padding: 18px 22px;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
        }
        .theme-analytics-section-accent {
          width: 12px;
          height: 44px;
          border-radius: 999px;
          align-self: stretch;
        }
        .theme-analytics-section-accent-chatbot {
          background: linear-gradient(180deg, #4f46e5 0%, #8b5cf6 100%);
        }
        .theme-analytics-section-accent-visitor {
          background: linear-gradient(180deg, #0ea5e9 0%, #14b8a6 100%);
        }
        .theme-analytics-section-kicker {
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #64748b;
        }
        .theme-analytics-section-title {
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.04em;
          color: #0f172a;
          line-height: 1.05;
        }
        .theme-analytics-section-subtitle {
          margin-top: 4px;
          font-size: 13px;
          color: #64748b;
          line-height: 1.55;
        }
        .theme-analytics-chevron {
          width: 34px;
          height: 34px;
          border-radius: 999px;
          background: rgba(255,255,255,0.78);
          border: 1px solid rgba(226, 232, 240, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-analytics-section-body {
          padding: 0 22px 22px;
        }
        .theme-analytics-panel {
          padding: 18px;
          border-radius: 20px;
          border: 1px solid rgba(226, 232, 240, 0.95);
          background: rgba(255,255,255,0.76);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-analytics-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .theme-analytics-list-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 14px;
          background: rgba(248, 250, 252, 0.92);
          border: 1px solid rgba(226, 232, 240, 0.95);
        }
        .theme-query-table-wrap {
          border-radius: 20px;
          border: 1px solid rgba(191, 219, 254, 0.88);
          background: rgba(255,255,255,0.88);
          overflow: hidden;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.92);
        }
        .theme-query-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          table-layout: fixed;
        }
        .theme-query-table-head-row {
          background: linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.96) 100%);
        }
        .theme-query-table-head-cell {
          padding: 12px 14px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #64748b;
          border-bottom: 1px solid rgba(226, 232, 240, 0.95);
          position: sticky;
          top: 0;
          z-index: 1;
          backdrop-filter: blur(8px);
        }
        .theme-query-table-row td {
          border-bottom: 1px solid rgba(241, 245, 249, 0.95);
        }
        .theme-query-table tbody tr:nth-child(even) td {
          background: rgba(248, 250, 252, 0.65);
        }
        .theme-query-table tbody tr:hover td {
          background: rgba(239,246,255,0.52);
        }
        .theme-query-table td {
          padding: 14px;
          font-size: 13px;
          color: #334155;
          vertical-align: middle;
          line-height: 1.45;
        }
        .theme-query-cell-query {
          width: 38%;
          font-weight: 700;
          color: #0f172a;
          white-space: normal;
          word-break: break-word;
        }
        .theme-query-cell-kb {
          width: 27%;
          color: #334155;
          white-space: normal;
          word-break: break-word;
        }
        .theme-query-cell-center {
          width: 12%;
          text-align: center;
        }
        .theme-query-cell-end {
          width: 11%;
          text-align: right;
        }
        .theme-query-cell-hits {
          font-weight: 800;
          color: #0f172a;
        }
        .theme-query-cell-date {
          white-space: nowrap;
          color: #64748b;
          font-size: 12px;
        }
        .theme-query-cell-start {
          padding-left: 18px !important;
        }
        .theme-query-cell-last {
          padding-right: 18px !important;
        }
        .theme-query-status-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 88px;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .theme-query-status-pill-matched {
          background: rgba(220, 252, 231, 0.9);
          color: #166534;
        }
        .theme-query-status-pill-unmatched {
          background: rgba(254, 226, 226, 0.9);
          color: #b91c1c;
        }
        .theme-query-table-empty {
          color: #94a3b8;
        }
        .theme-empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 34px 22px;
          text-align: center;
          border-radius: 22px;
          background: linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%);
          border: 1px solid rgba(226, 232, 240, 0.95);
        }
        .theme-empty-state-icon {
          width: 52px;
          height: 52px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #4f46e5;
          background: linear-gradient(180deg, rgba(79, 70, 229, 0.12), rgba(14, 165, 233, 0.1));
          border: 1px solid rgba(165, 180, 252, 0.28);
        }
        .theme-analytics-data-card .Polaris-Card {
          border-radius: 22px;
        }
        .theme-analytics-progress-track {
          height: 10px;
          border-radius: 999px;
          overflow: hidden;
          background: rgba(226, 232, 240, 0.9);
        }
        .theme-analytics-progress-track-slim {
          height: 18px;
        }
        .theme-analytics-progress-fill {
          border-radius: inherit;
        }
        .theme-analytics-progress-fill-success {
          background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
        }
        .theme-analytics-progress-fill-warning {
          background: linear-gradient(90deg, #f59e0b 0%, #f97316 100%);
        }
        .theme-analytics-progress-fill-danger {
          background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
        }
        .theme-skeleton-bar {
          border-radius: 999px;
          background: linear-gradient(90deg, rgba(226,232,240,0.9) 0%, rgba(241,245,249,1) 50%, rgba(226,232,240,0.9) 100%);
        }
        .theme-analytics-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(148,163,184,0.3), transparent);
          margin: 2px 0 8px;
        }
        @media (max-width: 960px) {
          .theme-analytics-hero-grid {
            grid-template-columns: 1fr;
          }
          .theme-analytics-visual-grid {
            grid-template-columns: 1fr;
          }
          .theme-analytics-side-panel-stack {
            grid-template-columns: 1fr;
          }
          .theme-analytics-story-card-wide {
            grid-column: span 1;
          }
          .theme-analytics-story-card-full {
            grid-column: span 1;
          }
          .theme-analytics-story-visual-visitor,
          .theme-analytics-story-visual-perf {
            grid-template-columns: 1fr;
          }
          .theme-analytics-visitor-row,
          .theme-analytics-visitor-row-primary,
          .theme-analytics-visitor-row-secondary,
          .theme-analytics-perf-score-row {
            grid-template-columns: 1fr;
          }
          .theme-analytics-story-side-panel-activity {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .theme-analytics-story-side-panel-activity > .theme-analytics-inline-label,
          .theme-analytics-story-stack-stat-progress {
            grid-column: 1 / -1;
          }
          .theme-analytics-gap-panels {
            grid-template-columns: 1fr;
          }
          .theme-analytics-gap-panel-evidence > .Polaris-InlineStack,
          .theme-analytics-expanded-log-shell > .Polaris-InlineStack {
            gap: 0.75rem;
          }
          .theme-analytics-story-head-coverage {
            flex-direction: column;
            align-items: flex-start;
          }
          .theme-analytics-story-head {
            align-items: flex-start;
          }
          .theme-analytics-coverage-chip-row {
            justify-content: flex-start;
          }
          .theme-query-table {
            min-width: 44rem;
          }
          .theme-analytics-story-side-panel,
          .theme-analytics-story-stack-stats {
            padding-left: 1rem;
            border-left: 1px solid rgba(226, 232, 240, 0.95);
            padding-top: 1rem;
            border-top: 1px solid rgba(226, 232, 240, 0.95);
          }
        }
      `}</style>
      {isFree ? (
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <Text as="h2" variant="headingMd">
                    Analytics Preview
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Last 7 days
                  </Text>
                </BlockStack>
                <Badge>Free</Badge>
              </InlineStack>
              {analyticsLoading ? (
                <div className="theme-skeleton-bar" style={{ height: "24px", width: "30%" }} />
              ) : (
                <BlockStack gap="100">
                  <Text as="h1" variant="heading2xl">
                    {analyticsData?.totalInteractions ?? "—"}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Total widget interactions (opens + channel clicks)
                  </Text>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">
                Unlock full analytics with Growth
              </Text>
              <List>
                <List.Item>Widget opens, channel clicks &amp; device split</List.Item>
                <List.Item>90-day data retention &amp; return visitor rate</List.Item>
                <List.Item>Channel breakdown with period selector</List.Item>
              </List>
            </BlockStack>
            <br />
            <button
              onClick={onOpenPlansTab}
              style={{
                background: themeColor,
                border: "none",
                borderRadius: "10px",
                padding: "10px 16px",
                cursor: "pointer",
                color: "white",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "20px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
              }}
            >
              Upgrade to Reach Growth
            </button>
          </Card>
        </BlockStack>
      ) : (
        <div className="theme-analytics-shell" style={{ ["--analytics-accent" as string]: themeColor } as React.CSSProperties}>
          <BlockStack gap="500">
            <div className="theme-analytics-sticky">
              <div className="theme-analytics-hero-card">
                <Box padding="500">
                  <div className="theme-analytics-hero-grid">
                    <BlockStack gap="400">
                      <BlockStack gap="150">
                        <div className="theme-analytics-title-row">
                          <Text as="h2" variant="headingXl">
                            Analytics
                          </Text>
                          <span className="theme-analytics-title-period">Last {analyticsPeriod} days</span>
                        </div>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {isRepPlus
                            ? `Full analytics with daily trends for the last ${analyticsPeriod} days.`
                            : isAgentPlus
                              ? "Visitor and chatbot metrics in one streamlined dashboard."
                              : "Visitor engagement metrics with a cleaner, more actionable view."}
                        </Text>
                      </BlockStack>
                      <div className="theme-analytics-chip-row">
                        <div className="theme-analytics-stat-chip">
                          <span className="theme-analytics-stat-chip-label">Widget Opens</span>
                          <span className="theme-analytics-stat-chip-value">{analyticsData?.widgetOpens ?? "—"}</span>
                        </div>
                        {isAgentPlus && (
                          <div className="theme-analytics-stat-chip">
                            <span className="theme-analytics-stat-chip-label">Conversations</span>
                            <span className="theme-analytics-stat-chip-value">{analyticsData?.totalChats ?? "—"}</span>
                          </div>
                        )}
                        <div className="theme-analytics-stat-chip">
                          <span className="theme-analytics-stat-chip-label">Unique Visitors</span>
                          <span className="theme-analytics-stat-chip-value">{analyticsData?.uniqueVisitors ?? "—"}</span>
                        </div>
                      </div>
                    </BlockStack>
                    <div className="theme-analytics-side-panel-stack">
                      <div className="theme-analytics-side-panel">
                        <InlineStack gap="200" blockAlign="center" align="space-between">
                          <Text as="h3" variant="headingSm">
                            Controls
                          </Text>
                          <Badge tone={isRepPlus ? "success" : isAgentPlus ? "info" : undefined}>
                            {isRepPlus ? "Rep+" : isAgentPlus ? "Agent+" : "Growth+"}
                          </Badge>
                        </InlineStack>
                        <Select
                          label="Period"
                          labelHidden
                          options={[
                            { label: "Last 7 days", value: "7" },
                            { label: "Last 30 days", value: "30" },
                            { label: "Last 90 days", value: "90" },
                          ]}
                          value={analyticsPeriod}
                          onChange={setAnalyticsPeriod}
                        />
                        <button
                          className="theme-analytics-refresh-btn"
                          onClick={onRefresh}
                          disabled={analyticsLoading || isRefreshing}
                          title="Refresh analytics"
                          style={{
                            background: analyticsLoading || isRefreshing ? "#94a3b8" : themeColor,
                            border: "none",
                            borderRadius: "12px",
                            padding: "10px 14px",
                            cursor: analyticsLoading || isRefreshing ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            color: "white",
                            fontSize: "13px",
                            fontWeight: 700,
                            transition: "background 0.15s",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span style={{ display: "flex", animation: isRefreshing || analyticsLoading ? "reach-spin 0.8s linear infinite" : "none" }}>
                            <RefreshCw size={15} />
                          </span>
                          {isRefreshing || analyticsLoading ? "Refreshing…" : "Refresh Dashboard"}
                        </button>
                      </div>
                      <DataManagementCard onWipe={onShowAnalyticsWipeModal} />
                    </div>
                  </div>
                </Box>
              </div>
            </div>
            <style>{`@keyframes reach-spin { to { transform: rotate(360deg); } }`}</style>

            {analyticsLoading ? (
              <Card>
                <BlockStack gap="400">
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="theme-skeleton-bar" style={{ height: "20px", width: index === 1 ? "60%" : index === 2 ? "80%" : "40%" }} />
                  ))}
                </BlockStack>
              </Card>
            ) : analyticsData ? (
              <BlockStack gap="800">
                {analyticsData.widgetOpens === 0 && analyticsData.totalChats === 0 && analyticsData.channelClicks === 0 && (
                  <Card>
                    <div className="theme-empty-state">
                      <div className="theme-empty-state-icon">
                        <BarChart3 size={20} />
                      </div>
                      <BlockStack gap="150">
                        <Text as="h3" variant="headingSm">
                          No analytics yet
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Install the widget and visit your storefront once. This dashboard will fill in as visitors open the widget, chat, or click a channel.
                        </Text>
                      </BlockStack>
                      <InlineStack gap="200" align="center">
                        <button
                          type="button"
                          onClick={onOpenAppearanceTab}
                          style={{
                            background: themeColor,
                            border: "none",
                            borderRadius: "12px",
                            padding: "10px 14px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            color: "white",
                            fontSize: "13px",
                            fontWeight: 700,
                            lineHeight: "20px",
                            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
                          }}
                        >
                          Open appearance settings
                        </button>
                        <button
                          type="button"
                          onClick={onRefresh}
                          style={{
                            background: "#ffffff",
                            border: "1px solid rgba(203, 213, 225, 0.95)",
                            borderRadius: "12px",
                            padding: "10px 14px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            color: "#475569",
                            fontSize: "13px",
                            fontWeight: 700,
                            lineHeight: "20px",
                            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
                          }}
                        >
                          <RefreshCw size={15} />
                          Refresh metrics
                        </button>
                      </InlineStack>
                    </div>
                  </Card>
                )}

                <div className="theme-analytics-visual-grid">
                  <VisitorStoryCard
                    analyticsData={analyticsData}
                    analyticsPeriod={analyticsPeriod}
                    isRepPlus={isRepPlus}
                    themeColor={themeColor}
                  />
                  {isAgentPlus && <ChatbotStoryCard analyticsData={analyticsData} themeColor={themeColor} analyticsPeriod={analyticsPeriod} />}
                  {isAgentPlus && (
                    <GapStoryCard
                      kbGaps={kbGaps}
                      topTopics={analyticsData.topTopics}
                      topClickedSuggestions={analyticsData.topClickedSuggestions}
                      analyticsPeriod={analyticsPeriod}
                      queryLog={queryLog}
                      queryLogLoading={queryLogLoading}
                      showFullQueryLog={showFullQueryLog}
                      knowledgeGapDelta={toSafeNullableNumber(analyticsData.knowledgeGapDelta)}
                      onToggleFullQueryLog={() => setShowFullQueryLog((value) => !value)}
                    />
                  )}
                </div>

                {isRepPlus && isAgentPlus && (
                  <div className="theme-analytics-domain theme-analytics-domain-chatbot">
                    <div className="theme-analytics-domain-card">
                      <button
                        onClick={() => setChatbotSectionOpen((value) => !value)}
                        onMouseEnter={() => setChatbotSectionOpen(true)}
                        className="theme-analytics-section-toggle"
                      >
                        <div className="theme-analytics-section-accent theme-analytics-section-accent-chatbot" />
                        <div>
                          <div className="theme-analytics-section-kicker">Assistant Performance</div>
                          <div className="theme-analytics-section-title">Chatbot Intelligence</div>
                          <div className="theme-analytics-section-subtitle">How well is your chatbot serving customers, where it succeeds, and what it still misses.</div>
                        </div>
                        <div className="theme-analytics-chevron" style={{ transform: chatbotSectionOpen ? "rotate(0deg)" : "rotate(-90deg)" }}>
                          <ChevronDown size={16} />
                        </div>
                      </button>
                      <Collapsible open={chatbotSectionOpen} id="analytics-chatbot-section">
                        <div className="theme-analytics-section-body">
                          <BlockStack gap="300">
                            <Grid>
                              <Grid.Cell columnSpan={{ xs: 6, md: isRepPlus ? 4 : 6 }}>
                                <MetricCard
                                  label="Conversations Started"
                                  value={toSafeNumber(analyticsData.totalChats)}
                                  tip="Visitors who sent at least one message to your chatbot. Compare this to Widget Opens to gauge how compelling your chat entry point is."
                                />
                              </Grid.Cell>
                              <Grid.Cell columnSpan={{ xs: 6, md: isRepPlus ? 4 : 6 }}>
                                <MetricCard
                                  label="Messages Exchanged"
                                  value={toSafeNumber(analyticsData.totalMessages)}
                                  tip="Total messages sent across all conversations. Higher counts mean visitors are engaged and finding the chatbot worth continuing — not just bouncing after one message."
                                />
                              </Grid.Cell>
                              {isRepPlus && (
                                <Grid.Cell columnSpan={{ xs: 6, md: 4 }}>
                                  <MetricCard
                                    label="Avg Messages / Conv"
                                    value={toSafeNumber(analyticsData.totalChats) > 0 ? (toSafeNumber(analyticsData.totalMessages) / toSafeNumber(analyticsData.totalChats)).toFixed(1) : "N/A"}
                                    tip="Average back-and-forth per conversation. Higher depth means visitors are engaged. Very low depth may indicate the bot isn't helpful enough to keep them talking."
                                  />
                                </Grid.Cell>
                              )}
                            </Grid>

                            <div className="theme-analytics-panel">
                              <BlockStack gap="400">
                                <InlineStack align="space-between" blockAlign="start">
                                  <BlockStack gap="100">
                                    <InlineStack gap="150" blockAlign="center">
                                      <Text as="h3" variant="headingMd">
                                        Answer Match Rate
                                      </Text>
                                      <Tooltip content="The % of questions your chatbot matched to a knowledge base entry. This is your most important quality metric. Aim for 70%+. Improve it by reviewing 'Top Unanswered Questions' below and adding those entries to your KB." preferredPosition="above">
                                        <span style={{ cursor: "help" }} className="theme-icon-muted">
                                          <InfoIcon />
                                        </span>
                                      </Tooltip>
                                    </InlineStack>
                                    <Text as="p" variant="bodySm" tone="subdued">
                                      {toSafeNullableNumber(analyticsData.matchRate) === null
                                        ? "No chat messages recorded yet in this period."
                                        : toSafeNullableNumber(analyticsData.matchRate)! >= 70
                                          ? "Your chatbot is performing well. Keep your KB updated as your store evolves."
                                          : toSafeNullableNumber(analyticsData.matchRate)! >= 40
                                            ? "Room to improve. Check unanswered questions below and add KB entries."
                                            : "Your chatbot is missing most questions. Adding more KB entries will make a big difference."}
                                    </Text>
                                  </BlockStack>
                                  <Text
                                    as="h2"
                                    variant="headingXl"
                                    tone={
                                      toSafeNullableNumber(analyticsData.matchRate) === null
                                        ? "subdued"
                                        : toSafeNullableNumber(analyticsData.matchRate)! >= 70
                                          ? "success"
                                          : toSafeNullableNumber(analyticsData.matchRate)! >= 40
                                            ? "caution"
                                            : "critical"
                                    }
                                  >
                                    {toSafeNullableNumber(analyticsData.matchRate) !== null ? `${toSafeNullableNumber(analyticsData.matchRate)}%` : "N/A"}
                                  </Text>
                                </InlineStack>
                                {toSafeNullableNumber(analyticsData.matchRate) !== null && (
                                  <BlockStack gap="200">
                                    <div className="theme-analytics-progress-track">
                                      <div
                                        style={{ height: "100%", width: `${toSafeNullableNumber(analyticsData.matchRate)}%` }}
                                        className={`theme-analytics-progress-fill ${toSafeNullableNumber(analyticsData.matchRate)! >= 70 ? "theme-analytics-progress-fill-success" : toSafeNullableNumber(analyticsData.matchRate)! >= 40 ? "theme-analytics-progress-fill-warning" : "theme-analytics-progress-fill-danger"}`}
                                      />
                                    </div>
                                    <InlineStack gap="400">
                                      <Text as="p" variant="bodySm" tone="subdued">
                                        Answered: {toSafeNumber(analyticsData.matchedMessages)}
                                      </Text>
                                      <Text as="p" variant="bodySm" tone="subdued">
                                        Unanswered: {toSafeNumber(analyticsData.unmatchedMessages)}
                                      </Text>
                                    </InlineStack>
                                  </BlockStack>
                                )}
                              </BlockStack>
                            </div>

                            <Grid>
                              <Grid.Cell columnSpan={{ xs: 6, md: 4 }}>
                                <MetricCard
                                  label="Satisfaction Score"
                                  value={analyticsData.feedbackScore !== null ? `${analyticsData.feedbackScore}%` : "N/A"}
                                  tip="Thumbs-up as a % of all feedback clicks. A high score means your KB answers are accurate and helpful. Improve by reviewing unhelpful responses in the Chatbot tab."
                                  tone="success"
                                />
                              </Grid.Cell>
                              <Grid.Cell columnSpan={{ xs: 6, md: 4 }}>
                                <MetricCard
                                  label="Suggestion Clicks"
                                  value={toSafeNumber(analyticsData.suggestionClicks)}
                                  tip="How many times visitors clicked a quick-reply suggestion chip. High usage means your suggestions match intent. Low usage means they may not be relevant — adjust them in Chatbot settings."
                                />
                              </Grid.Cell>
                              <Grid.Cell columnSpan={{ xs: 6, md: 4 }}>
                                <MetricCard
                                  label="Suggestion Engagement Rate"
                                  value={toSafeNullableNumber(analyticsData.suggestionEngagementRate) != null ? `${toSafeNullableNumber(analyticsData.suggestionEngagementRate)}%` : "N/A"}
                                  tip="How often visitors clicked a suggestion chip after the assistant showed quick suggestions in a reply."
                                  tone="base"
                                />
                              </Grid.Cell>
                            </Grid>

                            {isRepPlus && analyticsData.dailyTrend && analyticsData.dailyTrend.length > 1 && (
                              <Grid>
                                <Grid.Cell columnSpan={{ xs: 6, md: 6 }}>
                                  <div className="theme-analytics-panel">
                                    <BlockStack gap="300">
                                      <BlockStack gap="050">
                                        <Text as="p" variant="bodySm" tone="subdued">
                                          Conversations / Day
                                        </Text>
                                        <Text as="h3" variant="headingLg">
                                          {Math.round(toSafeNumber(analyticsData.totalChats) / analyticsData.dailyTrend.length)} avg
                                        </Text>
                                      </BlockStack>
                                      <Sparkline data={analyticsData.dailyTrend.map((day: any) => day.chats)} color="var(--analytics-chatbot-accent)" />
                                    </BlockStack>
                                  </div>
                                </Grid.Cell>
                                <Grid.Cell columnSpan={{ xs: 6, md: 6 }}>
                                  <div className="theme-analytics-panel">
                                    <BlockStack gap="300">
                                      <BlockStack gap="050">
                                        <Text as="p" variant="bodySm" tone="subdued">
                                          Messages / Day
                                        </Text>
                                        <Text as="h3" variant="headingLg">
                                          {Math.round(toSafeNumber(analyticsData.totalMessages) / analyticsData.dailyTrend.length)} avg
                                        </Text>
                                      </BlockStack>
                                      <Sparkline data={analyticsData.dailyTrend.map((day: any) => day.messages)} color="#8b5cf6" />
                                    </BlockStack>
                                  </div>
                                </Grid.Cell>
                              </Grid>
                            )}

                            {analyticsData.topTopics && analyticsData.topTopics.length > 0 && (
                              <div className="theme-analytics-panel">
                                <BlockStack gap="300">
                                  <InlineStack align="space-between" blockAlign="center">
                                    <InlineStack gap="200" blockAlign="center">
                                      <Text as="h3" variant="headingSm">
                                        Top Matched Topics
                                      </Text>
                                      <Badge tone="success">KB hits</Badge>
                                    </InlineStack>
                                  </InlineStack>
                                  <Text as="p" variant="bodySm" tone="subdued">
                                    Your most-answered knowledge base topics for this period. These are working — keep them updated.
                                  </Text>
                                  <div className="theme-analytics-list">
                                    {analyticsData.topTopics.map((topic: any, index: number) => (
                                      <div key={topic.kbId || index} className="theme-analytics-list-row">
                                        <Text as="p" variant="bodySm">
                                          {topic.title}
                                        </Text>
                                        <Badge tone="info">{`${topic.matchCount}×`}</Badge>
                                      </div>
                                    ))}
                                  </div>
                                </BlockStack>
                              </div>
                            )}

                            {kbGaps.length > 0 && (
                              <div className="theme-analytics-panel">
                                <BlockStack gap="300">
                                  <InlineStack align="space-between" blockAlign="center">
                                    <InlineStack gap="200" blockAlign="center">
                                      <Text as="h3" variant="headingSm">
                                        Top Unanswered Questions
                                      </Text>
                                      <Badge tone="attention">{`${kbGaps.length} gaps`}</Badge>
                                    </InlineStack>
                                  </InlineStack>
                                  <Text as="p" variant="bodySm" tone="subdued">
                                    Real questions visitors asked that your chatbot couldn&apos;t answer. Add them to your knowledge base to improve match rate and reduce escalations.
                                  </Text>
                                  <div className="theme-analytics-list">
                                    {kbGaps.slice(0, 8).map((gap, index) => (
                                      <div key={gap.id || index} className="theme-analytics-list-row">
                                        <Text as="p" variant="bodySm">
                                          {gap.question}
                                        </Text>
                                        <Badge tone="warning">{`${gap.hitCount}×`}</Badge>
                                      </div>
                                    ))}
                                  </div>
                                  {kbGaps.length > 8 && (
                                    <Text as="p" variant="bodySm" tone="subdued">
                                      +{kbGaps.length - 8} more — view all in the Chatbot tab.
                                    </Text>
                                  )}
                                </BlockStack>
                              </div>
                            )}

                            <div className="theme-analytics-panel">
                              <BlockStack gap="300">
                                <InlineStack align="space-between" blockAlign="center">
                                  <InlineStack gap="200" blockAlign="center">
                                    <Text as="h3" variant="headingSm">
                                      Query Activity Log
                                    </Text>
                                    <Badge tone="info">Agent+</Badge>
                                  </InlineStack>
                                </InlineStack>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  Every query visitors asked your chatbot, grouped by text. Use this to identify recurring gaps and refine your knowledge base.
                                </Text>
                                {queryLogLoading ? (
                                  <div style={{ padding: "12px 0" }}>
                                    <Text as="p" variant="bodySm" tone="subdued">
                                      Loading…
                                    </Text>
                                  </div>
                                ) : queryLog.length === 0 ? (
                                  <div style={{ padding: "12px 0", textAlign: "center" }}>
                                    <Text as="p" variant="bodySm" tone="subdued">
                                      No chatbot queries recorded in this period.
                                    </Text>
                                  </div>
                                ) : (
                                  <div className="theme-query-table-wrap">
                                    <table className="theme-query-table">
                                      <thead>
                                        <tr className="theme-query-table-head-row">
                                          <th className="theme-query-table-head-cell theme-query-cell-start">Query</th>
                                          <th className="theme-query-table-head-cell">KB Match</th>
                                          <th className="theme-query-table-head-cell theme-query-cell-center">Status</th>
                                          <th className="theme-query-table-head-cell theme-query-cell-end">Hits</th>
                                          <th className="theme-query-table-head-cell theme-query-cell-end theme-query-cell-last">Last Asked</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {queryLog.slice(0, 50).map((row, index) => (
                                          <tr key={index} className={index < queryLog.slice(0, 50).length - 1 ? "theme-query-table-row" : undefined}>
                                            <td className="theme-query-cell-start theme-query-cell-query" title={row.queryText}>
                                              {row.queryText}
                                            </td>
                                            <td className="theme-query-table-kb theme-query-cell-kb" title={row.kbTitle || "—"}>
                                              {row.kbTitle || <span className="theme-query-table-empty">—</span>}
                                            </td>
                                            <td className="theme-query-cell-center">
                                              <span className={`theme-query-status-pill ${row.isMatched ? "theme-query-status-pill-matched" : "theme-query-status-pill-unmatched"}`}>
                                                {row.isMatched ? "matched" : "unmatched"}
                                              </span>
                                            </td>
                                            <td className="theme-query-cell-end theme-query-cell-hits">{row.hits}</td>
                                            <td className="theme-query-table-last theme-query-cell-end theme-query-cell-last theme-query-cell-date">
                                              {row.lastAsked ? new Date(row.lastAsked).toLocaleDateString() : "—"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {queryLog.length > 50 && (
                                      <div style={{ paddingTop: "8px" }}>
                                        <Text as="p" variant="bodySm" tone="subdued">
                                          Showing top 50 of {queryLog.length} queries.
                                        </Text>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </BlockStack>
                            </div>
                          </BlockStack>
                        </div>
                      </Collapsible>
                    </div>
                  </div>
                )}

                {isRepPlus && (
                  <>
                    <div className="theme-analytics-divider" />
                    <div className="theme-analytics-domain theme-analytics-domain-visitor">
                      <div className="theme-analytics-domain-card">
                        <button
                          onClick={() => setVisitorSectionOpen((value) => !value)}
                          onMouseEnter={() => setVisitorSectionOpen(true)}
                          className="theme-analytics-section-toggle"
                        >
                          <div className="theme-analytics-section-accent theme-analytics-section-accent-visitor" />
                          <div>
                            <div className="theme-analytics-section-kicker">Audience Insights</div>
                            <div className="theme-analytics-section-title">Visitor Intelligence</div>
                            <div className="theme-analytics-section-subtitle">Extended visitor detail, daily trend data, and deeper operational breakdowns.</div>
                          </div>
                          <div className="theme-analytics-chevron" style={{ transform: visitorSectionOpen ? "rotate(0deg)" : "rotate(-90deg)" }}>
                            <ChevronDown size={16} />
                          </div>
                        </button>
                        <Collapsible open={visitorSectionOpen} id="analytics-visitor-section">
                          <div className="theme-analytics-section-body">
                            <BlockStack gap="300">
                              <Grid>
                                <Grid.Cell columnSpan={{ xs: 6, md: 3 }}>
                                  <MetricCard
                                    label="Widget Opens"
                                    value={analyticsData.widgetOpens ?? 0}
                                    tip="How many times visitors opened your widget. A growing number means your widget's placement and appearance are working. Test different button positions to increase visibility."
                                  />
                                </Grid.Cell>
                                <Grid.Cell columnSpan={{ xs: 6, md: 3 }}>
                                  <MetricCard
                                    label="Unique Visitors"
                                    value={analyticsData.uniqueVisitors ?? 0}
                                    tip="Distinct people who interacted with your widget, tracked per browser session. Shows your true audience reach — not just repeated clicks from the same person."
                                  />
                                </Grid.Cell>
                                <Grid.Cell columnSpan={{ xs: 6, md: 3 }}>
                                  <MetricCard
                                    label="Channel Clicks"
                                    value={toSafeNumber(analyticsData.channelClicks)}
                                    tip="Visitors who clicked a support channel (WhatsApp, email, etc.) to escalate beyond the bot. High counts signal your chatbot isn't fully covering customer needs."
                                  />
                                </Grid.Cell>
                                <Grid.Cell columnSpan={{ xs: 6, md: 3 }}>
                                  <MetricCard
                                    label="Channel Click-Through Rate"
                                    value={toSafeNullableNumber(analyticsData.channelClickThroughRate) != null ? `${toSafeNullableNumber(analyticsData.channelClickThroughRate)}%` : "—"}
                                    tip="How often visitors who opened Reach chose a support channel without turning it into a harsh conversion score."
                                  />
                                </Grid.Cell>
                              </Grid>

                              <Grid>
                                <Grid.Cell columnSpan={{ xs: 6, md: 4 }}>
                                  <MetricCard
                                    label="Mobile Opens"
                                    value={analyticsData.deviceSplit?.mobile ?? 0}
                                    tip="Widget opens from mobile devices. High mobile traffic means you should prioritize your mobile button placement and size."
                                  />
                                </Grid.Cell>
                                <Grid.Cell columnSpan={{ xs: 6, md: 4 }}>
                                  <MetricCard
                                    label="Desktop Opens"
                                    value={analyticsData.deviceSplit?.desktop ?? 0}
                                    tip="Widget opens from desktop devices. Compare with mobile to understand where your customers are engaging from."
                                  />
                                </Grid.Cell>
                                <Grid.Cell columnSpan={{ xs: 6, md: 4 }}>
                                  <MetricCard
                                    label="Return Visitor Rate"
                                    value={toSafeNullableNumber(analyticsData.returnVisitorRate) != null ? `${toSafeNullableNumber(analyticsData.returnVisitorRate)}%` : "—"}
                                    tip="Percentage of visitors who had a previous session before this period. A higher rate means customers are finding value and coming back."
                                  />
                                </Grid.Cell>
                              </Grid>

                              {analyticsData.dailyTrend && analyticsData.dailyTrend.length > 1 && (
                                <div className="theme-analytics-panel">
                                  <BlockStack gap="300">
                                    <BlockStack gap="050">
                                      <Text as="p" variant="bodySm" tone="subdued">
                                        Widget Opens / Day
                                      </Text>
                                      <Text as="h3" variant="headingLg">
                                        {Math.round((analyticsData.widgetOpens ?? 0) / analyticsData.dailyTrend.length)} avg
                                      </Text>
                                    </BlockStack>
                                    <Sparkline data={analyticsData.dailyTrend.map((day: any) => day.opens)} color="var(--analytics-visitor-accent)" />
                                  </BlockStack>
                                </div>
                              )}
                            </BlockStack>
                          </div>
                        </Collapsible>
                      </div>
                    </div>
                  </>
                )}
              </BlockStack>
            ) : null}
          </BlockStack>
        </div>
      )}

    </BlockStack>
  );
}
